import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { useForegroundTiming } from '../hooks/useForegroundTiming';
import { MonacoEditorComponent } from '../components/Editor/MonacoEditor';
import { OutputPanel } from '../components/Editor/OutputPanel';
import { ChatInterface } from '../components/Chat/ChatInterface';
import { TiptapEditor } from '../components/Editor/TiptapEditor';
import { DashboardLayout } from '../components/DashboardLayout';
import glassTheme from '../styles/glassTheme';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { renderToString } from 'katex';
import { FLAT_TOPICS, TOPICS_HIERARCHY } from '../data/excelTopics';
import { userIsAdmin } from '../services/rbacApi';
import * as teacherApi from '../services/teacherApi';
import type { Course } from '../services/teacherApi';
import * as topicsApi from '../services/topicsApi';
import toast from 'react-hot-toast';
import { recordClientActivity, ClientActivityType } from '../services/activityApi';
import { persistTopicCompletion } from '../services/topicCompletion';

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
const recordLearningBoundary = (
  type: ClientActivityType,
  target: { kind: 'course' | 'topic'; id: string }
) => {
  if (!OBJECT_ID_PATTERN.test(target.id)) return;
  void recordClientActivity(type, target).catch(() => {
    console.warn('[activity-tracking-failed]', { type });
  });
};

const Container = styled.div`
  width: 100%;
  max-width: none;
  padding: 0;
  box-sizing: border-box;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 2rem 0;
  margin-bottom: 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeroDescription = styled.p`
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 2rem;
`;

const GlassCard = styled.div`
  ${glassTheme.glass.container}
  padding: ${glassTheme.spacing.lg};
  margin-bottom: ${glassTheme.spacing.lg};

  @media (max-width: 768px) {
    padding: ${glassTheme.spacing.md};
  }

  @media (prefers-reduced-motion: reduce) {
    &, * {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: ${glassTheme.spacing.md};
  margin-bottom: ${glassTheme.spacing.md};
  flex-wrap: wrap;
`;

const TabButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>`
  padding: 0.8rem 1.5rem;
  background: transparent;
  border: none;
  border-radius: 0;
  border-bottom: 2px solid ${props => props.active ? glassTheme.colors.accent.blue : 'transparent'};
  color: ${props => props.active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.fast};
  font-size: 1rem;

  &:hover {
    color: ${glassTheme.colors.text.primary};
    border-bottom-color: ${glassTheme.colors.accent.blue};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

interface TabContentProps {
  active: boolean;
  $isPractice?: boolean;
}

const TabContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})<TabContentProps>`
  display: ${props => props.active ? 'flex' : 'none'};
  flex-direction: column;
  height: auto;
  min-height: auto;
  animation: fadeIn 0.5s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ConceptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ConceptCard = styled.div`
  ${glassTheme.glass.light}
  padding: 1.5rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.02);
    box-shadow: ${glassTheme.shadow.glow};
  }
`;

const ConceptTitle = styled.h3`
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  font-size: 1.3rem;
`;

const Formula = styled.div`
  background: rgba(0, 0, 0, 0.4);
  padding: 1rem;
  border-radius: ${glassTheme.borderRadius.md};
  margin: 1rem 0;
  text-align: center;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.primary};
  border: 1px solid ${glassTheme.colors.border};
`;

const EditorContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${glassTheme.spacing.md};
  height: clamp(560px, 68dvh, 760px);
  overflow: hidden;

  > * {
    min-width: 0;
    min-height: 0;
    height: 100%;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    height: clamp(560px, 65dvh, 680px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;

    > *:first-child { height: 440px; }
    > *:last-child { height: 380px; }
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: ${glassTheme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${glassTheme.colors.border};
`;

const StatsTh = styled.th`
  padding: 1rem;
  text-align: left;
  background: rgba(0, 0, 0, 0.5);
  font-weight: bold;
  color: ${glassTheme.colors.text.primary};
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const StatsTd = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.secondary};
`;

const StatsTr = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;

// Compact topic button grid for all topics
const CompactTopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const CompactTopicButton = styled.button<{ $active?: boolean }>`
  ${glassTheme.glass.light}
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.$active ? glassTheme.colors.accent.blue : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  background: ${props => props.$active ? 'rgba(96, 165, 250, 0.15)' : 'rgba(0, 0, 0, 0.3)'};
  color: ${glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
    background: rgba(96, 165, 250, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const CompactTopicTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.3;
`;

// Hierarchical topic sections
const TopicSection = styled.div`
  margin-bottom: 2rem;
`;

const MainCategoryHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${props => props.$color};
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.$color};
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const SubCategoryHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.$color};
  margin: 1rem 0 0.5rem 0;
  opacity: 0.9;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &::before {
    content: '└';
    margin-right: 0.25rem;
  }
`;

const CollapseIcon = styled.span<{ $collapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 0.6rem;
  transition: transform 0.3s ease;
  transform: rotate(${props => props.$collapsed ? '-90deg' : '0deg'});
  flex-shrink: 0;
`;

const TopicGrid = styled.div<{ $collapsed: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.6rem;
  margin-left: 1rem;
  max-height: ${props => props.$collapsed ? '0' : 'none'};
  overflow: ${props => props.$collapsed ? 'hidden' : 'visible'};
  opacity: ${props => props.$collapsed ? '0' : '1'};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

// Tooltip for topic description on hover
const TopicTooltip = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: fixed;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid ${glassTheme.colors.accent.blue};
  border-radius: ${glassTheme.borderRadius.md};
  padding: 1rem;
  max-width: 300px;
  z-index: 10000;
  display: ${props => props.$visible ? 'block' : 'none'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  pointer-events: none;
`;

const TooltipTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
  margin-bottom: 0.5rem;
`;

const TooltipDescription = styled.div`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.5;
`;

// Tooltip for course metadata on hover
const CourseTooltip = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: fixed;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translateY(-100%);
  margin-top: -10px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid ${glassTheme.colors.accent.blue};
  border-radius: ${glassTheme.borderRadius.md};
  padding: 1rem;
  max-width: 320px;
  z-index: 10000;
  display: ${props => props.$visible ? 'block' : 'none'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid rgba(0, 0, 0, 0.95);
  }
`;

const CourseTooltipTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
  margin-bottom: 0.75rem;
`;

const CourseTooltipMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
`;

const CourseTooltipMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CourseTooltipDescription = styled.div`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.primary};
  line-height: 1.5;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

// Topic card styled components for the concepts tab
const TopicCard = styled.div<{ onClick?: () => void }>`
  ${glassTheme.glass.light}
  padding: 1rem;
  border-radius: ${glassTheme.borderRadius.md};
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(96, 165, 250, 0.15);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
    border-color: ${glassTheme.colors.accent.blue};
  }
`;

const TopicHeader = styled.div`
  margin-bottom: 0.5rem;
`;

const TopicTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  line-height: 1.4;
`;

const TopicDescription = styled.div`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.4;
  margin-bottom: 0.5rem;
`;

const TopicMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${glassTheme.colors.text.muted};
`;

const CompletionCheckbox = styled.div<{ $completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.$completed
    ? '#4ade80'
    : '#9ca3af'};
  background: ${props => props.$completed
    ? '#4ade80'
    : '#ffffff'};
  color: ${props => props.$completed
    ? '#ffffff'
    : 'transparent'};
  font-size: 0.85rem;
  font-weight: 900;
  cursor: default;
  pointer-events: none;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;
  line-height: 1;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${glassTheme.spacing.xxl} ${glassTheme.spacing.lg};
  text-align: center;
  color: ${glassTheme.colors.text.secondary};
`;

const CourseSectionHeader = styled.div`
  margin-bottom: ${glassTheme.spacing.md};
`;

const CourseSectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${glassTheme.spacing.sm};
`;

const CourseSectionTitle = styled.h2`
  margin: 0;
  color: ${glassTheme.colors.text.primary};
  font-size: 1.5rem;
  line-height: 1.3;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
    border-radius: ${glassTheme.borderRadius.sm};
  }
`;

const CourseSectionDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  margin: ${glassTheme.spacing.xs} 0 0;
`;

const CourseStatus = styled.div`
  ${glassTheme.glass.light}
  padding: ${glassTheme.spacing.xxl} ${glassTheme.spacing.lg};
  color: ${glassTheme.colors.text.secondary};
  text-align: center;

  button {
    margin-inline: auto;
  }
`;

const CourseSkeleton = styled.div`
  ${glassTheme.glass.light}
  min-height: 300px;
  border-radius: ${glassTheme.borderRadius.lg};
  background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.1), rgba(255,255,255,.04));
  background-size: 200% 100%;
  animation: skeleton 1.4s ease infinite;

  @keyframes skeleton {
    to { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Definition = styled.div`
  width: 100%;
  font-size: 1rem;
  line-height: 1.8;
  color: ${glassTheme.colors.text.primary};
  background: rgba(52, 152, 219, 0.1);
  padding: 1rem;
  border-left: 4px solid ${glassTheme.colors.accent.blue};
  border-radius: ${glassTheme.borderRadius.sm};
  margin-bottom: 1.5rem;
`;

const KeyPoints = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;

  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
    color: ${glassTheme.colors.text.primary};
    line-height: 1.6;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: ${glassTheme.colors.accent.blue};
      font-weight: bold;
    }
  }
`;

const FormulaBox = styled.div`
  width: 100%;
  background: rgba(46, 204, 113, 0.1);
  border-left: 4px solid #27ae60;
  padding: 1rem;
  border-radius: ${glassTheme.borderRadius.sm};
  margin-bottom: 1rem;

  .formula {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: #2c3e50;
  }

  .explanation {
    font-size: 0.9rem;
    color: ${glassTheme.colors.text.secondary};
    line-height: 1.5;
  }
`;

const ExampleBox = styled.div`
  width: 100%;
  background: rgba(241, 196, 15, 0.1);
  border-left: 4px solid #f39c12;
  padding: 1rem;
  border-radius: ${glassTheme.borderRadius.sm};
  margin-bottom: 1rem;

  .title {
    font-weight: 600;
    color: #d68910;
    margin-bottom: 0.5rem;
  }

  .content {
    color: ${glassTheme.colors.text.primary};
    line-height: 1.6;
    font-size: 0.95rem;
  }
`;

const ApplicationsList = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;

  li {
    position: relative;
    padding-left: 1.8rem;
    margin-bottom: 0.5rem;
    color: ${glassTheme.colors.text.primary};
    line-height: 1.5;

    &::before {
      content: '▸';
      position: absolute;
      left: 0;
      color: ${glassTheme.colors.accent};
      font-weight: bold;
    }
  }
`;

const SummaryBox = styled.div`
  width: 100%;
  background: linear-gradient(135deg, rgba(155, 89, 182, 0.1) 0%, rgba(142, 68, 173, 0.1) 100%);
  border-left: 4px solid #9b59b6;
  padding: 1.5rem;
  border-radius: ${glassTheme.borderRadius.md};
  margin-top: 1rem;

  .title {
    font-weight: 600;
    color: #8e44ad;
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  .content {
    color: ${glassTheme.colors.text.primary};
    line-height: 1.8;
    font-size: 0.95rem;
  }
`;

const ContentPlaceholder = styled.div`
  width: 100%;
  text-align: center;
  padding: 3rem 2rem;
  color: ${glassTheme.colors.text.secondary};
  background: rgba(255, 255, 255, 0.03);
  border-radius: ${glassTheme.borderRadius.lg};
  border: 2px dashed rgba(255, 255, 255, 0.1);

  p {
    margin: 0.5rem 0;
    font-size: 1.1rem;
  }
`;

// Modal styled components - Full-screen interactive card
const ModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: stretch;
  justify-content: stretch;
  z-index: 1000;
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.3s ease;
  padding: 3rem;
`;

const ModalContainer = styled.div<{ $visible: boolean }>`
  ${glassTheme.glass.container}
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s ease;
`;

// Left sidebar for topic navigation
const TopicSidebar = styled.div`
  width: 280px;
  min-width: 280px;
  background: rgba(0, 0, 0, 0.4);
  border-right: 1px solid ${glassTheme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  height: 55.68px;
  padding: 0 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const SidebarTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: ${glassTheme.colors.text.primary};
`;

const SidebarCloseButton = styled.button`
  background: transparent;
  border: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: ${glassTheme.colors.status.error};
    border-color: ${glassTheme.colors.status.error};
    transform: rotate(90deg);
  }
`;

const TopicList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 0;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 3px;
  }
`;

const TopicCategoryLabel = styled.div<{ $color: string; $isSubCategory?: boolean }>`
  padding: ${props => props.$isSubCategory ? '0.35rem 1.25rem 0.25rem 1.75rem' : '0.5rem 1.25rem 0.35rem'};
  font-size: ${props => props.$isSubCategory ? '0.9rem' : '1rem'};
  font-weight: 600;
  text-transform: ${props => props.$isSubCategory ? 'none' : 'uppercase'};
  letter-spacing: ${props => props.$isSubCategory ? 'normal' : '0.05em'};
  color: ${props => props.$color};
  border-left: 3px solid ${props => props.$color};
  margin-top: ${props => props.$isSubCategory ? '0.25rem' : '0.5rem'};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const TopicListItem = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 0.65rem 1.25rem 0.65rem 2.5rem;
  background: ${props => props.$active
    ? 'rgba(96, 165, 250, 0.15)'
    : 'transparent'};
  border: none;
  border-left: 3px solid ${props => props.$active
    ? glassTheme.colors.accent.blue
    : 'transparent'};
  color: ${glassTheme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  position: relative;
  opacity: ${props => props.$active ? '1' : '0.8'};

  &:hover {
    background: rgba(96, 165, 250, 0.1);
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${glassTheme.colors.accent.blue};
    opacity: ${props => props.$active ? '1' : '0'};
  }
`;

const TopicListItemText = styled.span`
  flex: 1;
  font-size: 0.8rem;
  line-height: 1.3;
  color: ${glassTheme.colors.text.primary};
`;

const TopicListItemMeta = styled.span`
  font-size: 0.7rem;
  color: ${glassTheme.colors.text.muted};
  white-space: nowrap;
`;

// Main content area
const ModalContentArea = styled.div<{ $fullscreen?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  ${props => props.$fullscreen && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2000;
    background: rgba(15, 15, 35, 0.98);
    backdrop-filter: blur(20px);
  `}
`;

const ModalHeader = styled.div`
  height: 55.68px;
  padding: 0 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  gap: 1rem;
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: ${glassTheme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ModalMeta = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TestButton = styled.button`
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, ${glassTheme.colors.accent.purple} 0%, ${glassTheme.colors.accent.pink} 100%);
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(192, 132, 252, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EditButton = styled.button<{ $isEditing?: boolean }>`
  padding: 0.5rem 1rem;
  background: ${props => props.$isEditing
    ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'};
  border: none;
  border-radius: ${glassTheme.borderRadius.md};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.$isEditing
      ? '0 4px 12px rgba(74, 222, 128, 0.4)'
      : '0 4px 12px rgba(245, 158, 11, 0.4)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FullscreenButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CourseNavButton = styled.button`
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: ${glassTheme.borderRadius.md};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.secondary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: ${glassTheme.colors.text.muted};
  }
`;

const EditModeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: ${glassTheme.borderRadius.md};
  color: #f59e0b;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;

  &::before {
    content: '✏️';
  }
`;

const ModalContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 4px;

    &:hover {
      background: ${glassTheme.colors.accent.cyan};
    }
  }
`;

const ModalSection = styled.div`
  margin-bottom: 1.5rem;
  max-width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Enrollment Modal Styles
const EnrollModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const EnrollModalContainer = styled.div<{ $visible: boolean }>`
  ${glassTheme.glass.heavy}
  width: 90%;
  max-width: 500px;
  border-radius: ${glassTheme.borderRadius.lg};
  padding: 2rem;
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
  border: 1px solid ${glassTheme.colors.border};
`;

const EnrollModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EnrollModalInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  background: rgba(0, 0, 0, 0.3);
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${glassTheme.colors.accent.blue};
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const EnrollModalError = styled.div<{ $visible: boolean }>`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.75rem;
  display: ${props => props.$visible ? 'block' : 'none'};
`;

const EnrollModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const EnrollButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: ${glassTheme.borderRadius.md};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$primary
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${glassTheme.colors.text.primary};
  opacity: ${props => props.$primary ? 1 : 0.9};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    opacity: 1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EnrollCourseButton = styled.button`
  ${glassTheme.glass.light}
  padding: 0.625rem 1.25rem;
  border: 1px solid ${glassTheme.colors.accent.blue};
  border-radius: ${glassTheme.borderRadius.md};
  background: rgba(96, 165, 250, 0.1);
  color: ${glassTheme.colors.accent.blue};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(96, 165, 250, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(96, 165, 250, 0.3);
  }
`;

// Course Description Modal Styles
const CourseDescModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.3s ease;
`;

const CourseDescModalContent = styled.div`
  ${glassTheme.glass.light}
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  border-radius: 20px;
  border: 1px solid ${glassTheme.colors.border};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const CourseDescModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(102, 126, 234, 0.1);
`;

const CourseDescModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: ${glassTheme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CourseDescModalClose = styled.button`
  background: transparent;
  border: none;
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${glassTheme.colors.text.primary};
    transform: rotate(90deg);
  }
`;

const CourseDescModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.7);
  }
`;

const CourseDescTooltip = styled.span<{ $visible?: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  padding: 0.4rem 0.6rem;
  background: rgba(0, 0, 0, 0.95) !important;
  color: white !important;
  font-size: 0.75rem;
  white-space: nowrap;
  border-radius: 6px;
  opacity: ${props => props.$visible ? 1 : 0} !important;
  visibility: ${props => props.$visible ? 'visible' : 'hidden'} !important;
  transform: translateX(-50%) ${props => props.$visible ? 'translateY(4px)' : 'translateY(8px)'} !important;
  transition: all 0.2s ease;
  z-index: 99999;
  font-weight: 500;
  pointer-events: none;
`;

const CourseDescInfoIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
`;

const CourseDescInfoIcon = styled.button`
  background: transparent;
  border: none;
  color: ${glassTheme.colors.accent.blue};
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(96, 165, 250, 0.15);
    transform: scale(1.1);
  }
`;


// Course View Styles for Students
const CoursesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${glassTheme.spacing.md};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CourseCard = styled.div<{ $accent?: string }>`
  ${glassTheme.glass.light}
  border-radius: ${glassTheme.borderRadius.lg};
  width: 100%;
  min-width: 0;
  padding: ${glassTheme.spacing.lg};
  transition: all ${glassTheme.transition.base};
  border: 1px solid ${glassTheme.colors.border};
  border-top: 4px solid ${props => props.$accent || glassTheme.colors.accent.blue};
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }
`;

const CourseHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const CourseIcon = styled.div<{ $color?: string }>`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: ${glassTheme.borderRadius.lg};
  background: ${props => props.$color || glassTheme.colors.accent.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const CourseTitle = styled.div`
  flex: 1;
`;

const CourseName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 0.35rem 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CourseCode = styled.code`
  background: rgba(0, 0, 0, 0.15);
  padding: 0.2rem 0.5rem;
  border-radius: ${glassTheme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
  border: 1px solid ${glassTheme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CourseDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.8rem;
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CourseProgress = styled.div`
  margin-bottom: 0.75rem;
`;

const CourseProgressBar = styled.div<{ $progress: number }>`
  height: 4px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${glassTheme.borderRadius.full};
  overflow: hidden;
  position: relative;
  margin-bottom: 0.35rem;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
    border-radius: ${glassTheme.borderRadius.full};
    transition: width 0.5s ease;
  }
`;

const CourseProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8125rem;
  color: ${glassTheme.colors.text.secondary};
  font-weight: 500;
`;

const CourseMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const CourseMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${glassTheme.colors.text.secondary};
  padding: 0.3rem 0.5rem;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${glassTheme.borderRadius.sm};
  border-left: 2px solid ${glassTheme.colors.accent.blue};

  i {
    font-size: 0.75rem;
    color: ${glassTheme.colors.accent.blue};
    width: 14px;
    text-align: center;
  }

  span {
    flex: 1;
  }
`;

const CourseStartButton = styled.button`
  width: 100%;
  padding: 0.65rem 1.25rem;
  background: ${glassTheme.colors.accent.blue};
  border: none;
  border-radius: ${glassTheme.borderRadius.md};
  color: white;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  box-shadow: 0 4px 12px rgba(96, 165, 250, 0.3);
  margin-top: auto;

  &:hover {
    background: ${glassTheme.colors.accent.blue};
    filter: brightness(1.1);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(96, 165, 250, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  span:first-child {
    font-size: 0.9rem;
  }
`;

const ChapterSection = styled.div<{ $expanded: boolean }>`
  border-top: 1px solid ${glassTheme.colors.border};
`;

const ChapterHeader = styled.div<{ $expanded: boolean; $hasSections: boolean }>`
  padding: 0.875rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  background: ${props => props.$expanded ? 'rgba(0, 0, 0, 0.2)' : 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  ${props => props.$hasSections && `
    padding-left: 2rem;
  `}
`;

const ChapterTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${glassTheme.colors.text.primary};
  flex: 1;
`;

const ChapterContent = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(0, 0, 0, 0.15);
`;

const SectionHeader = styled.div<{ $clickable?: boolean }>`
  padding: 0.625rem 1.25rem;
  padding-left: 3rem;
  font-size: 0.875rem;
  color: ${glassTheme.colors.text.secondary};
  border-top: 1px solid ${glassTheme.colors.border};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${props => props.$clickable && `
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  `}
`;

const CourseTopicList = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${props => props.$expanded ? '0.5rem 0' : '0'};
`;

const CourseTopicItem = styled.div<{ $completed: boolean }>`
  padding: 0.625rem 1.25rem;
  padding-left: 4rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 2px solid transparent;

  &:hover {
    background: rgba(96, 165, 250, 0.1);
    border-left-color: ${glassTheme.colors.accent.blue};
  }

  ${props => props.$completed && `
    opacity: 0.7;
  `}
`;

const TopicName = styled.span<{ $completed: boolean }>`
  flex: 1;
  font-size: 0.875rem;
  color: ${props => props.$completed ? glassTheme.colors.text.muted : glassTheme.colors.text.primary};
  text-decoration: ${props => props.$completed ? 'line-through' : 'none'};
`;

// HTML Content container for Tiptap content
const HTMLContent = styled.div`
  flex: 1;
  width: 100%;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 1rem;

  h1, h2, h3, h4, h5, h6 {
    line-height: 1.2;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin-top: 1.5em;
    margin-bottom: 0.75em;
  }

  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }

  p {
    margin-bottom: 0.75em;
  }

  ul, ol {
    padding-left: 1.5rem;
    margin-bottom: 0.75em;
  }

  li {
    margin-bottom: 0.25em;
  }

  strong, b {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }

  em, i {
    font-style: italic;
  }

  code {
    background: rgba(0, 0, 0, 0.4);
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 0.875em;
    color: #f472b6;
    font-family: 'Courier New', monospace;
  }

  pre {
    background: rgba(0, 0, 0, 0.6);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin-bottom: 1rem;

    code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }

  blockquote {
    border-left: 4px solid #8b5cf6;
    padding-left: 1rem;
    margin: 1rem 0;
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
  }

  a {
    color: #8b5cf6;
    text-decoration: underline;

    &:hover {
      color: #a78bfa;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;

    th, td {
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 0.5rem 0.75rem;
      text-align: left;
    }

    th {
      background: rgba(139, 92, 246, 0.2);
      font-weight: 600;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
  }

  hr {
    border: none;
    border-top: 2px solid rgba(255, 255, 255, 0.1);
    margin: 2rem 0;
  }

  mark {
    background: rgba(139, 92, 246, 0.3);
    padding: 0.1em 0.2em;
    border-radius: 3px;
  }
`;

// Sandboxed iframe for rendering HTML with CSS and JavaScript
const SandboxedHTMLContainer = styled.div<{ $autoHeight?: boolean }>`
  flex: 1;
  width: 100%;
  min-height: ${props => props.$autoHeight ? '400px' : '100%'};
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${glassTheme.colors.border};
  background: rgba(0, 0, 0, 0.3);

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
`;

// Component to render HTML in a sandboxed iframe
const SandboxedHTMLContent: React.FC<{ html: string }> = ({ html }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Prepare the full HTML content
  const hasDoctype = html.trim().toLowerCase().startsWith('<!doctype');
  const hasHtmlTag = html.toLowerCase().includes('<html');

  let fullHtml = html;

  // If it's a fragment, wrap it in a complete document
  if (!hasDoctype || !hasHtmlTag) {
    fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Content Preview</title>
  <style>
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      margin: 0;
      padding: 20px;
      line-height: 1.6;
      color: #333;
    }
  </style>
</head>
<body>
  ${html}
  <script>
    // Auto-resize script for iframe content
    (function() {
      window.addEventListener('load', function() {
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        window.parent.postMessage({ type: 'resize', height: height }, '*');
      });
    })();
  </script>
</body>
</html>`;
  }

  useEffect(() => {
    // Listen for messages from the iframe for height updates
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'resize' && containerRef.current) {
        const iframe = containerRef.current.querySelector('iframe');
        if (iframe) {
          iframe.style.height = `${Math.max(event.data.height, 400)}px`;
          if (containerRef.current) {
            (containerRef.current as HTMLElement).style.height = `${Math.max(event.data.height, 400)}px`;
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <SandboxedHTMLContainer ref={containerRef} $autoHeight={true}>
      <iframe
        ref={iframeRef}
        title="Content Preview"
        srcDoc={fullHtml}
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
        loading="lazy"
      />
    </SandboxedHTMLContainer>
  );
};

// Learning start overlay - covers content until student clicks start
const LearningStartOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 2rem;
`;

const LearningStartContent = styled.div`
  text-align: center;
  max-width: 600px;
`;

const LearningStartTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LearningStartDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const StartLearningButton = styled.button`
  padding: 1rem 3rem;
  font-size: 1.2rem;
  font-weight: 600;
  background: ${glassTheme.gradients.primary};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 0 30px rgba(96, 165, 250, 0.5), 0 0 60px rgba(192, 132, 252, 0.3);
  }

  &:active {
    transform: translateY(0) scale(1);
  }
`;

// Collapsible sidebar toggle button
const SidebarToggleButton = styled.button<{ $collapsed: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: none;
  border-top: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: ${glassTheme.colors.text.primary};
  }
`;

// Learning status toggle section
const LearningStatusSection = styled.div`
  ${glassTheme.glass.light}
  margin-top: 2rem;
  padding: 1.25rem 1.5rem;
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const LearningStatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const LearningStatusIcon = styled.span`
  font-size: 1.5rem;
`;

const LearningStatusLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LearningStatusTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
`;

const LearningStatusDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
`;

const LearningStatusSwitch = styled.button<{ $learned: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  border: 2px solid ${props => props.$learned ? '#4ade80' : '#9ca3af'};
  background: ${props => props.$learned ? '#4ade80' : 'transparent'};
  color: ${props => props.$learned ? '#ffffff' : glassTheme.colors.text.secondary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    border-color: #4ade80;
    background: ${props => props.$learned ? '#22c55e' : 'rgba(74, 222, 128, 0.1)'};
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LearningStatusIndicator = styled.span<{ $learned: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$learned ? '#ffffff' : '#9ca3af'};
`;

// Inline self-explanation section
const SelfExplanationSection = styled.div`
  ${glassTheme.glass.light}
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
`;

const SectionDivider = styled.div`
  height: 1px;
  background: ${glassTheme.colors.border};
  margin: 2rem 0;
`;

const SelfExplanationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SelfExplanationLabel = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.cyan};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InlineExplanationTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  font-size: 0.95rem;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.cyan};
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const ExplanationActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const SavedStatus = styled.span<{ $visible: boolean }>`
  font-size: 0.85rem;
  color: ${glassTheme.colors.status.success};
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  gap: 0.25rem;
`;

// Quiz section
const QuizSection = styled.div`
  ${glassTheme.glass.light}
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
`;

const QuizHeader = styled.div`
  margin-bottom: 1rem;
`;

const QuizLabel = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.purple};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuizLink = styled.button<{ $disabled: boolean }>`
  background: transparent;
  border: none;
  color: ${props => props.$disabled ? glassTheme.colors.text.muted : glassTheme.colors.accent.purple};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    color: ${glassTheme.colors.accent.pink};
    text-decoration: underline;
  }
`;

const QuizHint = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.muted};
  font-style: italic;
`;

// Keyword highlighting and popup modal
const KeywordHighlight = styled.span<{ $importance?: string }>`
  color: ${props => {
    switch (props.$importance) {
      case 'essential': return '#f59e0b'; // Amber for essential
      case 'important': return '#8b5cf6'; // Purple for important
      case 'optional': return '#6b7280'; // Gray for optional
      default: return '#8b5cf6';
    }
  }};
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px dotted ${props => {
    switch (props.$importance) {
      case 'essential': return '#f59e0b';
      case 'important': return '#8b5cf6';
      case 'optional': return '#6b7280';
      default: return '#8b5cf6';
    }
  }};
  padding: 0 2px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${props => {
      switch (props.$importance) {
        case 'essential': return 'rgba(245, 158, 11, 0.2)';
        case 'important': return 'rgba(139, 92, 246, 0.2)';
        case 'optional': return 'rgba(107, 114, 128, 0.2)';
        default: return 'rgba(139, 92, 246, 0.2)';
      }
    }};
    border-bottom-style: solid;
  }
`;

const KeywordTooltip = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: fixed;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translateY(-100%) translateX(-50%);
  margin-top: -10px;
  z-index: 3000;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.2s ease;
  max-width: 350px;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px 8px 0;
    border-style: solid;
    border-color: rgba(30, 30, 30, 0.95) transparent transparent transparent;
  }
`;

const KeywordTooltipContent = styled.div`
  ${glassTheme.glass.container}
  padding: 1rem 1.25rem;
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
`;

const KeywordTerm = styled.div<{ $importance?: string }>`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: ${props => {
    switch (props.$importance) {
      case 'essential': return '#f59e0b';
      case 'important': return '#8b5cf6';
      case 'optional': return '#6b7280';
      default: return '#8b5cf6';
    }
  }};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const KeywordDefinition = styled.div`
  font-size: 0.9rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.5;
`;

const KeywordImportanceBadge = styled.span<{ $level: string }>`
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: ${glassTheme.borderRadius.sm};
  background: ${props => {
    switch (props.$level) {
      case 'essential': return 'rgba(245, 158, 11, 0.2)';
      case 'important': return 'rgba(139, 92, 246, 0.2)';
      case 'optional': return 'rgba(107, 114, 128, 0.2)';
      default: return 'rgba(139, 92, 246, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$level) {
      case 'essential': return '#f59e0b';
      case 'important': return '#8b5cf6';
      case 'optional': return '#6b7280';
      default: return '#8b5cf6';
    }
  }};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KeywordAIExplanation = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${glassTheme.colors.border};
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.muted};
  line-height: 1.4;
  font-style: italic;
`;

const GenerateAIButton = styled.button`
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 6px;
  color: #8b5cf6;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  &:hover {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const KeywordsListSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  ${glassTheme.glass.light}
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
`;

const KeywordsListHeader = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.cyan};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const KeywordsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const KeywordChip = styled.button<{ $importance: string }>`
  padding: 0.4rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  background: ${props => {
    switch (props.$importance) {
      case 'essential': return 'rgba(245, 158, 11, 0.15)';
      case 'important': return 'rgba(139, 92, 246, 0.15)';
      case 'optional': return 'rgba(107, 114, 128, 0.15)';
      default: return 'rgba(139, 92, 246, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$importance) {
      case 'essential': return 'rgba(245, 158, 11, 0.3)';
      case 'important': return 'rgba(139, 92, 246, 0.3)';
      case 'optional': return 'rgba(107, 114, 128, 0.3)';
      default: return 'rgba(139, 92, 246, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.$importance) {
      case 'essential': return '#f59e0b';
      case 'important': return '#8b5cf6';
      case 'optional': return '#6b7280';
      default: return '#8b5cf6';
    }
  }};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => {
      switch (props.$importance) {
        case 'essential': return '0 4px 12px rgba(245, 158, 11, 0.3)';
        case 'important': return '0 4px 12px rgba(139, 92, 246, 0.3)';
        case 'optional': return '0 4px 12px rgba(107, 114, 128, 0.3)';
        default: return '0 4px 12px rgba(139, 92, 246, 0.3)';
      }
    }};
  }
`;

// Demo code section
const DemoCodeSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  ${glassTheme.glass.light}
  border-radius: ${glassTheme.borderRadius.lg};
  border: 1px solid ${glassTheme.colors.border};
`;

const DemoCodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DemoCodeTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.cyan};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DemoCodeButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DemoCodeLanguageButton = styled.button<{ $active?: boolean }>`
  padding: 0.4rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  background: ${props => props.$active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.15)'};
  border: 1px solid ${props => props.$active ? 'rgba(34, 197, 94, 0.4)' : 'rgba(107, 114, 128, 0.3)'};
  color: ${props => props.$active ? '#22c55e' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(34, 197, 94, 0.3)' : 'rgba(107, 114, 128, 0.25)'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GenerateDemoCodeButton = styled.button<{ $loading?: boolean }>`
  padding: 0.4rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%);
  border: 1px solid rgba(34, 197, 94, 0.4);
  color: #22c55e;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(16, 185, 129, 0.3) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  ${props => props.$loading && `
    animation: pulse 1.5s ease-in-out infinite;
  `}
`;

const DemoCodeContent = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: ${glassTheme.borderRadius.md};
  padding: 1rem;
  overflow-x: auto;
`;

const DemoCodeBlock = styled.pre`
  margin: 0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #e5e7eb;
  white-space: pre-wrap;

  code {
    background: transparent;
    padding: 0;
    color: inherit;
  }
`;

const DemoCodeExplanation = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: ${glassTheme.borderRadius.md};
  border-left: 3px solid rgba(34, 197, 94, 0.5);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  line-height: 1.6;

  strong {
    color: #22c55e;
    font-weight: 600;
  }
`;

// Quiz popup modal - Full screen takeover
const QuizModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.98);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: stretch;
  justify-content: stretch;
  z-index: 2000;
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.3s ease;
`;

const QuizModalContainer = styled.div<{ $visible: boolean }>`
  ${glassTheme.glass.container}
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s ease;
  padding: 2rem;
`;

const QuizModalHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.3);
`;

const QuizModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuizProgress = styled.span`
  font-size: 0.9rem;
  color: ${glassTheme.colors.text.secondary};
  font-weight: 500;
`;

const QuizModalCloseButton = styled.button`
  background: transparent;
  border: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${glassTheme.colors.status.error};
    border-color: ${glassTheme.colors.status.error};
    transform: rotate(90deg);
  }
`;

const QuizModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.purple};
    border-radius: 4px;

    &:hover {
      background: ${glassTheme.colors.accent.pink};
    }
  }
`;

const QuestionText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 1.5rem 0;
  font-weight: 500;
`;

const QuizOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const QuizOption = styled.button<{ $selected: boolean; $correct?: boolean; $wrong?: boolean }>`
  padding: 0.875rem 1.25rem;
  background: ${props => {
    if (props.$correct) return 'rgba(74, 222, 128, 0.2)';
    if (props.$wrong) return 'rgba(248, 113, 113, 0.2)';
    return 'rgba(0, 0, 0, 0.4)';
  }};
  border: 2px solid ${props => {
    if (props.$correct) return '#4ade80';
    if (props.$wrong) return '#f87171';
    if (props.$selected) return glassTheme.colors.accent.purple;
    return glassTheme.colors.border;
  }};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
    border-color: ${glassTheme.colors.accent.purple};
    transform: translateX(2px);
  }

  &:disabled {
    cursor: ${props => props.$correct || props.$wrong ? 'default' : 'not-allowed'};
    opacity: ${props => props.$correct || props.$wrong ? '1' : '0.6'};
    &:hover {
      transform: none;
    }
  }
`;

const OptionLabel = styled.span<{ $selected: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? glassTheme.colors.accent.purple : glassTheme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;

  ${props => props.$selected && `
    background: ${glassTheme.colors.accent.purple};
    color: white;
    border-color: ${glassTheme.colors.accent.purple};
  `}
`;

const QuizModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.3);
`;

const QuizNavButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const QuizNavButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 0.6rem 1.25rem;
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
    : 'transparent'};
  border: ${props => props.$variant === 'primary' ? 'none' : `1px solid ${glassTheme.colors.border}`};
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    ${props => props.$variant === 'primary' && `
      box-shadow: 0 4px 12px rgba(168, 85, 247, 0.4);
    `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuizResultSection = styled.div<{ $visible: boolean }>`
  text-align: center;
  padding: 1rem 0;
`;

const QuizScore = styled.div`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const QuizScoreLabel = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 1rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;
`;

const RetryButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, ${glassTheme.colors.accent.green} 0%, ${glassTheme.colors.accent.cyan} 100%);
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 222, 128, 0.4);
  }
`;

const ConsecutiveCorrectDisplay = styled.div<{ $passed: boolean }>`
  margin-top: 1.5rem;
  padding: 1rem;
  background: ${props => props.$passed
    ? 'linear-gradient(135deg, rgba(74, 222, 128, 0.2) 0%, rgba(34, 197, 94, 0.2) 100%)'
    : 'rgba(0, 0, 0, 0.3)'};
  border: 2px solid ${props => props.$passed ? '#4ade80' : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  text-align: center;

  ${props => props.$passed && `
    animation: pulse 2s ease-in-out infinite;
  `}

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.4);
    }
    50% {
      box-shadow: 0 0 20px 5px rgba(74, 222, 128, 0.2);
    }
  }
`;

const ConsecutiveCorrectTitle = styled.div<{ $passed: boolean }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.$passed ? '#4ade80' : glassTheme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ConsecutiveCorrectCount = styled.div<{ $passed: boolean }>`
  font-size: 2rem;
  font-weight: 800;
  color: ${props => props.$passed ? '#4ade80' : glassTheme.colors.accent.purple};
  margin: 0.5rem 0;
`;

const ConsecutiveCorrectDescription = styled.div`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.4;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  margin-top: 1rem;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div<{ $progress: number; $passed: boolean }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: ${props => props.$passed
    ? 'linear-gradient(90deg, #4ade80 0%, #22c55e 100%)'
    : 'linear-gradient(90deg, #f59e0b 0%, #f97316 100%)'};
  border-radius: 4px;
  transition: width 0.5s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const SelfExplanationButton = styled.button`
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, ${glassTheme.colors.accent.cyan} 0%, ${glassTheme.colors.accent.blue} 100%);
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(34, 211, 238, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SelfExplanationSaveButton = styled.button<{ $disabled: boolean }>`
  padding: 0.6rem 1.25rem;
  background: ${props => props.$disabled
    ? 'rgba(74, 222, 128, 0.3)'
    : 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled
      ? 'none'
      : '0 4px 12px rgba(74, 222, 128, 0.4)'};
  }
`;

// Old styled components (kept for cleanup)
const ExplanationModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1050;
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.3s ease;
  padding: 2rem;
`;

const ExplanationModalContainer = styled.div<{ $visible: boolean }>`
  ${glassTheme.glass.container}
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const ExplanationModalHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ExplanationModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.cyan};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExplanationModalCloseButton = styled.button`
  background: transparent;
  border: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${glassTheme.colors.status.error};
    border-color: ${glassTheme.colors.status.error};
  }
`;

const ExplanationModalContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 3px;
  }
`;

const ExplanationTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.cyan};
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const ExplanationModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const SaveButton = styled.button<{ $disabled: boolean }>`
  padding: 0.6rem 1.5rem;
  background: ${props => props.$disabled
    ? 'rgba(74, 222, 128, 0.3)'
    : 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: white;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled
      ? 'none'
      : '0 4px 12px rgba(74, 222, 128, 0.4)'};
  }
`;

const SavedIndicator = styled.span<{ $visible: boolean }>`
  font-size: 0.8rem;
  color: ${glassTheme.colors.status.success};
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

type TabType = 'concepts' | 'practice' | 'ai-assistant';

// Use the hierarchical structure from TOPICS_HIERARCHY
// Main categories (6大类)
const MAIN_CATEGORIES = TOPICS_HIERARCHY.map(mainCat => ({
  id: mainCat.code,
  name: mainCat.chinese_name,
  englishName: mainCat.name,
  color: mainCat.color,
  icon: mainCat.icon,
  code: mainCat.code,
  topics_count: mainCat.topics_count
}));

// For backward compatibility with sidebar filtering - get subcategories
const SUBCATEGORIES = TOPICS_HIERARCHY.flatMap(mainCat =>
  mainCat.subcategories.map(subCat => ({
    id: subCat.code,
    name: subCat.chinese_name,
    englishName: subCat.name,
    color: mainCat.color,
    icon: mainCat.icon,
    code: subCat.code,
    mainCategoryCode: mainCat.code,
    mainCategoryName: mainCat.chinese_name,
    topics_count: subCat.topics_count
  }))
);

interface Category {
  _id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  order: number;
}

interface Topic {
  _id: string;
  title: string;
  slug: string;
  description: string;
  category: Category;
  order: number;
}

interface CategoryWithTopics {
  category: Category;
  topics: Topic[];
}

const CONCEPTS = [
  {
    id: 'descriptive',
    title: '描述性统计',
    description: '描述性统计用于总结和描述数据的基本特征。',
    formula: '\\bar{x} = \\frac{1}{n}\\sum_{i=1}^{n}x_i',
    explanation: '均值公式：所有观测值的总和除以观测次数。'
  },
  {
    id: 'standard-deviation',
    title: '标准差',
    description: '衡量数据离散程度的重要指标。',
    formula: '\\sigma = \\sqrt{\\frac{1}{n}\\sum_{i=1}^{n}(x_i - \\bar{x})^2}',
    explanation: '标准差越大，数据越分散。'
  },
  {
    id: 'hypothesis-testing',
    title: '假设检验',
    description: '用于判断样本与总体之间差异是否显著。',
    formula: 't = \\frac{\\bar{x} - \\mu_0}{s/\\sqrt{n}}',
    explanation: 't检验统计量计算公式。'
  }
];

const DISTRIBUTIONS = [
  { type: '正态分布', params: 'μ, σ²', mean: 'μ', variance: 'σ²', application: '自然现象、测量误差' },
  { type: '二项分布', params: 'n, p', mean: 'np', variance: 'np(1-p)', application: '成功/失败试验' },
  { type: '泊松分布', params: 'λ', mean: 'λ', variance: 'λ', application: '稀有事件计数' },
  { type: '卡方分布', params: 'k', mean: 'k', variance: '2k', application: '拟合优度检验' }
];

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
}

interface Keyword {
  term: string;
  definition: string;
  importance: 'essential' | 'important' | 'optional';
  aiExplanation?: string;
}

interface DemoCode {
  code: string;
  explanation: string;
  language: string;
}

interface TopicContent {
  contentRevision?: string;
  id: string;
  _id?: string;  // MongoDB ObjectId for database operations
  code: string;
  title: string;
  chineseTitle: string;
  description: string;
  categoryCode: string;
  mainCategoryCode: string;  // 所属大类代码
  mainCategoryName: string;  // 所属大类名称
  subCategoryCode: string;   // 所属子类代码
  subCategoryName: string;   // 所属子类名称
  content?: {
    definition: string;
    keyPoints: string[];
    formulas?: Array<{ formula: string; explanation: string }>;
    examples: Array<{ title: string; content: string }>;
    applications: string[];
    summary: string;
  };
  htmlContent?: string;  // HTML content from Tiptap editor
  keywords?: Keyword[];
  quiz?: QuizQuestion[];
  demoCode?: DemoCode;
}

interface CatalogCategory {
  code: string;
  chinese_name: string;
  color: string;
  icon: string;
  topics_count: number;
  subcategories: Array<{
    code: string;
    chinese_name: string;
  }>;
}

const FULL_TOPIC_LEARNING_ROLES = new Set([
  'super_admin',
  'admin',
  'content_manager',
  'teacher',
]);

// 基于 Excel 数据生成主题内容（使用新的层级结构）
const TOPICS_DATA: TopicContent[] = FLAT_TOPICS.map(topic => ({
  id: topic.id,
  code: topic.code,
  title: topic.title,
  chineseTitle: topic.chineseTitle,
  description: topic.description,
  categoryCode: topic.subCategoryCode,  // 使用子类代码作为主要分类
  mainCategoryCode: topic.mainCategoryCode,
  mainCategoryName: topic.mainCategoryName,
  subCategoryCode: topic.subCategoryCode,
  subCategoryName: topic.subCategoryName
}));

export const LearningPlatform: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, token } = useAuthStore();
  const hasFullTopicLearningAccess = Boolean(
    user?.isAdmin ||
    FULL_TOPIC_LEARNING_ROLES.has(user?.primaryRole || '') ||
    user?.roles?.some(role => FULL_TOPIC_LEARNING_ROLES.has(role.name))
  );
  const isStudentView = !hasFullTopicLearningAccess && Boolean(
    user?.primaryRole === 'student' ||
    user?.roles?.some(role => role.name === 'student')
  );
  const [activeTab, setActiveTab] = useState<TabType>('concepts');
  const [selectedTopic, setSelectedTopic] = useState<TopicContent | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selfExplanation, setSelfExplanation] = useState('');
  const [savedExplanation, setSavedExplanation] = useState('');
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isContentFullscreen, setIsContentFullscreen] = useState(false);

  // Collapsible categories state - default all collapsed
  const [collapsedMainCategories, setCollapsedMainCategories] = useState<Set<string>>(
    new Set(TOPICS_HIERARCHY.map(cat => cat.code))
  );
  const [collapsedSubCategories, setCollapsedSubCategories] = useState<Set<string>>(
    new Set(TOPICS_HIERARCHY.flatMap(cat => cat.subcategories.map(sub => sub.code)))
  );

  // Quiz states
  const [isQuizModalVisible, setIsQuizModalVisible] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<(number | null)[]>([]);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  // Demo code states
  const [isGeneratingDemoCode, setIsGeneratingDemoCode] = useState(false);

  // Learning progress states
  const [learningStarted, setLearningStarted] = useState(false);
  const [learningStartTime, setLearningStartTime] = useState<number | null>(null);
  const [completedTopics, setCompletedTopics] = useState<Set<string>>(new Set());

  // Content editing states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Database topics cache (stores topics loaded from database with htmlContent)
  const [dbTopics, setDbTopics] = useState<Map<string, TopicContent>>(new Map());

  // Enrolled courses state for students
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [enrolledTopicIds, setEnrolledTopicIds] = useState<Set<string>>(new Set());
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseLoadError, setCourseLoadError] = useState(false);
  const [courseReloadKey, setCourseReloadKey] = useState(0);
  const [mongoIdToTopicMap, setMongoIdToTopicMap] = useState<Map<string, TopicContent>>(new Map());
  const [catalogCategories, setCatalogCategories] = useState<CatalogCategory[]>(TOPICS_HIERARCHY);
  const [catalogTopics, setCatalogTopics] = useState<TopicContent[]>(TOPICS_DATA);
  const courseSectionTitleRef = useRef<HTMLHeadingElement>(null);
  const enrollInputRef = useRef<HTMLInputElement>(null);
  const enrollTriggerRef = useRef<HTMLButtonElement | null>(null);
  const viewedTopicIdsRef = useRef<Set<string>>(new Set());

  // Course enrollment modal state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollCourseCode, setEnrollCourseCode] = useState('');
  const [enrollError, setEnrollError] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // Course description modal state
  const [showCourseDescModal, setShowCourseDescModal] = useState(false);
  const [selectedCourseDesc, setSelectedCourseDesc] = useState<{ title: string; description: string } | null>(null);
  const [hoveredCourseTooltip, setHoveredCourseTooltip] = useState<{ courseId: string; rect: { top: number; left: number; width: number; height: number } } | null>(null);

  // Course view expansion state for students
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Topic tooltip state for hover description
  const [topicTooltip, setTopicTooltip] = useState<{
    visible: boolean;
    topic: TopicContent | null;
    x: number;
    y: number;
  }>({
    visible: false,
    topic: null,
    x: 0,
    y: 0
  });

  // Course tooltip state for hover metadata
  const [courseTooltip, setCourseTooltip] = useState<{
    visible: boolean;
    course: any;
    x: number;
    y: number;
  }>({
    visible: false,
    course: null,
    x: 0,
    y: 0
  });
  const courseTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Course context for learning (when learning from a specific course)
  const [learningCourse, setLearningCourse] = useState<any | null>(null);
  const [explanationFocused, setExplanationFocused] = useState(false);
  const [demonstrationFocused, setDemonstrationFocused] = useState(false);
  const [readyTopicFrame, setReadyTopicFrame] = useState('');
  const topicFrameKey = `${selectedTopic?._id || ''}/${selectedTopic?.contentRevision || 'unversioned'}`;
  const topicUsesFrame = /<(script|style)[\s>]/i.test(selectedTopic?.htmlContent || '');
  const topicTimingReady = isModalVisible && learningStarted && !isEditMode && !!selectedTopic &&
    !!(selectedTopic.htmlContent || selectedTopic.content) &&
    (!topicUsesFrame || readyTopicFrame === topicFrameKey);
  // This modal does not select a course activity assignment. Keep its topic time
  // standalone even when opened from the course sidebar.
  useForegroundTiming(isStudentView && !showEnrollModal && !showCourseDescModal
    ? isQuizModalVisible && selectedTopic?._id
      ? {context: 'topic', topicId: selectedTopic._id, surface: showQuizResult ? 'review' : 'quiz'}
      : isModalVisible && selectedTopic?._id
        ? {context: 'topic', topicId: selectedTopic._id,
          surface: explanationFocused ? 'selfExplanation' : demonstrationFocused ? 'demonstration' : 'reading'}
        : activeTab === 'practice' || activeTab === 'ai-assistant'
          ? {context: 'platform', platformKey: 'workspace', surface: activeTab === 'practice' ? 'code' : 'chat'}
          : null
    : null, isQuizModalVisible ? !!selectedTopic?.quiz?.length : isModalVisible ? topicTimingReady : true,
    isModalVisible && !isQuizModalVisible ? selectedTopic?.contentRevision || null : null);

  // Keyword tooltip states
  const [keywordTooltip, setKeywordTooltip] = useState<{
    visible: boolean;
    keyword: Keyword | null;
    x: number;
    y: number;
  }>({
    visible: false,
    keyword: null,
    x: 0,
    y: 0
  });

  // Quiz consecutive correct tracking
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [quizPassed, setQuizPassed] = useState(false);
  const [quizSubmitResult, setQuizSubmitResult] = useState<any>(null);

  useEffect(() => {
    viewedTopicIdsRef.current.clear();
  }, [user?.id]);

  // Fetch enrolled courses for students
  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      // Only fetch for students
      if (isStudentView && token) {
        try {
          setLoadingCourses(true);
          setCourseLoadError(false);
          const courses = await teacherApi.getCourses({ enrolled: 'me' });
          setEnrolledCourses(courses);

          // Extract all topic IDs from enrolled courses (MongoDB _id values)
          // Also collect full topic objects from enrolled courses for topics not in database
          const mongoTopicIds = new Set<string>();
          const enrolledCourseTopics = new Map<string, any>(); // Store full topic objects from enrolled courses

          courses.forEach((course: Course) => {
            console.log('[fetchEnrolledCourses] Processing course:', course.title, 'Chapters:', course.chapters.length);
            course.chapters.forEach((chapter: any) => {
              console.log('[fetchEnrolledCourses] Chapter:', chapter.title, 'Sections:', chapter.sections?.length);
              // Add topics directly in chapters (can be string IDs or objects)
              chapter.topics?.forEach((topicItem: any) => {
                const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
                mongoTopicIds.add(topicId);
                console.log('[fetchEnrolledCourses] Chapter topic:', topicId);
                // If it's a full object, store it for later use
                if (typeof topicItem === 'object' && topicItem !== null && (topicItem.topicId || topicItem._id)) {
                  enrolledCourseTopics.set(topicItem.topicId || topicItem._id, topicItem);
                }
              });
              // Add topics in sections (can be string IDs or objects)
              chapter.sections?.forEach((section: any) => {
                console.log('[fetchEnrolledCourses] Section:', section.title, 'Topics count:', section.topics?.length);
                section.topics?.forEach((topicItem: any) => {
                  const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
                  mongoTopicIds.add(topicId);
                  console.log('[fetchEnrolledCourses] Section topic:', section.title, topicId);
                  // If it's a full object, store it for later use
                  if (typeof topicItem === 'object' && topicItem !== null && (topicItem.topicId || topicItem._id)) {
                    enrolledCourseTopics.set(topicItem.topicId || topicItem._id, topicItem);
                  }
                });
              });
            });
          });

          // Fetch all topics from database to map MongoDB _id to code
          const { topics: allDbTopics } = await topicsApi.getAllTopics({ published: true });

          // Create a mapping from MongoDB _id to topic code
          const idToCodeMap = new Map<string, string>();
          allDbTopics.forEach((topic: topicsApi.Topic) => {
            if (topic.slug) {
              idToCodeMap.set(topic._id, topic.slug);
            }
          });

          // Create a mapping from MongoDB _id to TopicContent
          const mongoIdToTopic = new Map<string, TopicContent>();
          FLAT_TOPICS.forEach(flatTopic => {
            const topicContent: TopicContent = {
              ...flatTopic,
              categoryCode: flatTopic.mainCategoryCode,
            };
            mongoIdToTopic.set(flatTopic.id, topicContent);
          });

          // Add MongoDB _id to TopicContent mapping
          allDbTopics.forEach((dbTopic: topicsApi.Topic) => {
            const flatTopic = FLAT_TOPICS.find(ft => ft.code === dbTopic.slug);
            if (flatTopic) {
              // If FLAT_TOPICS match exists, use that as the base
              const topicContent: TopicContent = {
                ...flatTopic,
                _id: dbTopic._id,
                categoryCode: flatTopic.mainCategoryCode,
              };
              mongoIdToTopic.set(dbTopic._id, topicContent);
            } else {
              // No FLAT_TOPICS match - create TopicContent directly from database topic
              // This ensures all database topics are in the map, not just those with FLAT_TOPICS matches
              const topicContent: TopicContent = {
                id: dbTopic._id,
                _id: dbTopic._id,
                code: dbTopic.slug || dbTopic._id,
                title: dbTopic.title,
                chineseTitle: dbTopic.title,
                description: dbTopic.description || '',
                content: {
                  definition: dbTopic.description || '',
                  keyPoints: [],
                  examples: [],
                  applications: [],
                  summary: dbTopic.description || '',
                },
                mainCategoryCode: dbTopic.category?.slug || 'uncategorized',
                mainCategoryName: dbTopic.category?.name || '未分类',
                subCategoryCode: '',
                subCategoryName: '',
                categoryCode: dbTopic.category?.slug || 'uncategorized',
              };
              mongoIdToTopic.set(dbTopic._id, topicContent);
              console.log('[fetchEnrolledCourses] Created TopicContent from dbTopic:', dbTopic._id, dbTopic.slug);
            }
          });

          // IMPORTANT: Add topics from enrolled courses that aren't in the database
          // This handles cases where enrolled courses have topics with different IDs than what's in the database
          enrolledCourseTopics.forEach((courseTopic: any, topicId: string) => {
            // Only add if not already in the map (not found in database)
            if (!mongoIdToTopic.has(topicId)) {
              const topicContent: TopicContent = {
                id: topicId,
                _id: topicId,
                code: courseTopic.slug || courseTopic.code || topicId,
                title: courseTopic.title || courseTopic.chineseTitle || 'Unknown Topic',
                chineseTitle: courseTopic.title || courseTopic.chineseTitle || 'Unknown Topic',
                description: courseTopic.description || '',
                content: {
                  definition: courseTopic.description || '',
                  keyPoints: courseTopic.keyPoints || [],
                  examples: courseTopic.examples || [],
                  applications: courseTopic.applications || [],
                  summary: courseTopic.summary || courseTopic.description || '',
                },
                mainCategoryCode: courseTopic.mainCategoryCode || courseTopic.category?.slug || 'uncategorized',
                mainCategoryName: courseTopic.mainCategoryName || courseTopic.category?.name || '未分类',
                subCategoryCode: courseTopic.subCategoryCode || '',
                subCategoryName: courseTopic.subCategoryName || '',
                categoryCode: courseTopic.categoryCode || courseTopic.category?.slug || 'uncategorized',
              };
              mongoIdToTopic.set(topicId, topicContent);
              console.log('[fetchEnrolledCourses] Created TopicContent from enrolled course topic:', topicId, courseTopic.title);
            }
          });

          console.log('[fetchEnrolledCourses] mongoIdToTopicMap size:', mongoIdToTopic.size);
          console.log('[fetchEnrolledCourses] Sample map entries:', Array.from(mongoIdToTopic.entries()).slice(0, 3));
          console.log('[fetchEnrolledCourses] All MongoDB _id keys in map:', Array.from(mongoIdToTopic.keys()).filter(k => k.length === 24 && /^[a-f0-9]{24}$/.test(k)));
          console.log('[fetchEnrolledCourses] mongoTopicIds from courses:', Array.from(mongoTopicIds).slice(0, 5));

          setMongoIdToTopicMap(mongoIdToTopic);

          // For enrolled topic IDs, use MongoDB _id values directly since we now create
          // TopicContent objects for all database topics (not just FLAT_TOPICS matches)
          // This ensures all enrolled course topics are counted, not just ones with FLAT_TOPICS matches
          const enrolledMongoTopicIds = new Set<string>(mongoTopicIds);
          console.log('[fetchEnrolledCourses] Total enrolled topic IDs:', mongoTopicIds.size);
          console.log('[fetchEnrolledCourses] Enrolled topic IDs (MongoDB _id):', Array.from(enrolledMongoTopicIds));

          setEnrolledTopicIds(enrolledMongoTopicIds);
        } catch (error) {
          console.error('Failed to fetch enrolled courses:', error);
          if ((error as any)?.response?.status === 401) {
            logout(false);
            navigate('/login', { replace: true });
            return;
          }
          setCourseLoadError(true);
        } finally {
          setLoadingCourses(false);
        }
      } else if (hasFullTopicLearningAccess && token) {
        try {
          setLoadingCourses(true);
          setCourseLoadError(false);
          const [{ categories }, firstTopicsPage] = await Promise.all([
            topicsApi.getAllCategories({ hierarchy: true, isActive: false }),
            topicsApi.getAllTopics({ published: false, page: 1, limit: 1000 })
          ]);
          const remainingPages = await Promise.all(
            Array.from(
              { length: Math.max(0, firstTopicsPage.pagination.pages - 1) },
              (_, pageIndex) => topicsApi.getAllTopics({
                published: false,
                page: pageIndex + 2,
                limit: 1000
              })
            )
          );
          const allTopics = [
            ...firstTopicsPage.topics,
            ...remainingPages.flatMap(page => page.topics)
          ];
          const categoryById = new Map(categories.map(category => [category._id, category]));
          const subcategoryById = new Map(
            categories.flatMap(category =>
              (category.subcategories || []).map(subcategory => [subcategory._id, subcategory] as const)
            )
          );
          const parentBySubcategoryId = new Map(
            categories.flatMap(category =>
              (category.subcategories || []).map(subcategory => [subcategory._id, category] as const)
            )
          );
          const categoryMap = new Map<string, CatalogCategory>();

          const ensureCatalogCategory = (
            code: string,
            name: string,
            color: string,
            icon: string
          ) => {
            if (!categoryMap.has(code)) {
              categoryMap.set(code, {
                code,
                chinese_name: name,
                color,
                icon,
                topics_count: 0,
                subcategories: []
              });
            }
            return categoryMap.get(code)!;
          };

          categories.forEach(category => {
            const catalogCategory = ensureCatalogCategory(
              category.code,
              category.chinese_name || category.name,
              category.color,
              category.icon
            );
            catalogCategory.subcategories = (category.subcategories || []).map(subcategory => ({
              code: subcategory.code,
              chinese_name: subcategory.chinese_name || subcategory.name
            }));
          });

          const mappedTopics = allTopics.map((topic): TopicContent => {
            const categoryId = topic.category?._id;
            const subcategoryId = typeof topic.subcategory === 'string'
              ? topic.subcategory
              : topic.subcategory?._id;
            let subcategory = subcategoryId ? subcategoryById.get(subcategoryId) : undefined;
            if (!subcategory && categoryId) {
              subcategory = subcategoryById.get(categoryId);
            }
            const mainCategory = (categoryId ? categoryById.get(categoryId) : undefined) ||
              (subcategory ? parentBySubcategoryId.get(subcategory._id) : undefined);
            const mainCategoryCode = mainCategory?.code || 'uncategorized';
            const mainCategoryName = mainCategory?.chinese_name || mainCategory?.name || '未分类';
            const catalogCategory = ensureCatalogCategory(
              mainCategoryCode,
              mainCategoryName,
              mainCategory?.color || '#64748b',
              mainCategory?.icon || '📂'
            );
            const subCategoryCode = subcategory?.code || `${mainCategoryCode}-other`;
            const subCategoryName = subcategory?.chinese_name || subcategory?.name || '其他主题';
            if (!catalogCategory.subcategories.some(item => item.code === subCategoryCode)) {
              catalogCategory.subcategories.push({
                code: subCategoryCode,
                chinese_name: subCategoryName
              });
            }
            catalogCategory.topics_count += 1;

            return {
              id: topic._id,
              _id: topic._id,
              code: topic.slug,
              title: topic.title,
              chineseTitle: topic.title,
              description: topic.description || '',
              categoryCode: subCategoryCode,
              mainCategoryCode,
              mainCategoryName,
              subCategoryCode,
              subCategoryName
            };
          });

          setCatalogCategories(Array.from(categoryMap.values()));
          setCatalogTopics(mappedTopics);
          setEnrolledTopicIds(new Set(mappedTopics.map(topic => topic.id)));
          setMongoIdToTopicMap(new Map(mappedTopics.map(topic => [topic.id, topic])));
        } catch (error) {
          console.error('Failed to fetch topic catalog:', error);
          if ((error as any)?.response?.status === 401) {
            logout(false);
            navigate('/login', { replace: true });
            return;
          }
          setCourseLoadError(true);
        } finally {
          setLoadingCourses(false);
        }
      } else {
        setCatalogCategories(TOPICS_HIERARCHY);
        setCatalogTopics(TOPICS_DATA);
        setEnrolledTopicIds(new Set(FLAT_TOPICS.map(topic => topic.id)));
      }
    };

    fetchEnrolledCourses();
  }, [hasFullTopicLearningAccess, isStudentView, token, courseReloadKey, logout, navigate]);

  useEffect(() => {
    if (showEnrollModal) {
      enrollInputRef.current?.focus();
    }
  }, [showEnrollModal]);

  // Fetch completed topics from StudentProfile
  useEffect(() => {
    const fetchCompletedTopics = async () => {
      if (!token) return;

      try {
        const API_BASE = (process.env as any).REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/learning-events/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[fetchCompletedTopics] API response:', data);
          if (data.success && data.data?.enrolledCourses) {
            // Collect all completed topic IDs from all enrolled courses
            const completedIds = new Set<string>();
            data.data.enrolledCourses.forEach((enrollment: any) => {
              console.log('[fetchCompletedTopics] Enrollment:', enrollment.courseId, 'progress:', enrollment.progress);
              if (enrollment.progress?.completedTopics) {
                enrollment.progress.completedTopics.forEach((topicId: any) => {
                  // Handle both string IDs and populated objects
                  let idStr: string;
                  if (typeof topicId === 'string') {
                    idStr = topicId;
                  } else if (topicId?._id) {
                    idStr = topicId._id.toString();
                  } else if (topicId?.id) {
                    idStr = topicId.id.toString();
                  } else {
                    console.warn('[fetchCompletedTopics] Invalid topic ID format:', topicId);
                    return;
                  }
                  console.log('[fetchCompletedTopics] Adding completed topic ID:', idStr);
                  completedIds.add(idStr);
                });
              }
            });
            console.log('[fetchCompletedTopics] Total completed topics:', completedIds.size, 'IDs:', Array.from(completedIds));
            setCompletedTopics(completedIds);
          }
        }
      } catch (error) {
        console.error('Failed to fetch completed topics:', error);
      }
    };

    fetchCompletedTopics();
  }, [token]);

  // Debug: log mongoIdToTopicMap
  useEffect(() => {
    console.log('[Debug] mongoIdToTopicMap size:', mongoIdToTopicMap.size);
    console.log('[Debug] mongoIdToTopicMap sample entries:', Array.from(mongoIdToTopicMap.entries()).slice(0, 3));
  }, [mongoIdToTopicMap]);

  // Debug: log selectedTopic and completedTopics
  useEffect(() => {
    if (selectedTopic) {
      console.log('[Debug] selectedTopic.id:', selectedTopic.id, 'selectedTopic._id:', selectedTopic._id, 'selectedTopic.title:', selectedTopic.title);
      console.log('[Debug] completedTopics:', Array.from(completedTopics));
      console.log('[Debug] Is completed?', selectedTopic._id && completedTopics.has(selectedTopic._id));
    }
  }, [selectedTopic, completedTopics]);

  // Helper function to get topic by MongoDB _id or code
  const getTopicByMongoId = (mongoId: string): TopicContent | null => {
    // First try to find in the mongoIdToTopicMap
    let topic = mongoIdToTopicMap.get(mongoId);
    if (topic) return topic;

    // Fallback: try to find in FLAT_TOPICS directly by id
    const flatTopic1 = FLAT_TOPICS.find(ft => ft.id === mongoId);
    if (flatTopic1) {
      const topicContent: TopicContent = {
        ...flatTopic1,
        categoryCode: flatTopic1.mainCategoryCode,
      };
      return topicContent;
    }

    // Fallback: try to find by code if mongoId looks like a code
    const flatTopic2 = FLAT_TOPICS.find(ft => ft.code === mongoId);
    if (flatTopic2) {
      const topicContent: TopicContent = {
        ...flatTopic2,
        categoryCode: flatTopic2.mainCategoryCode,
      };
      return topicContent;
    }

    // Enhanced debugging: show actual keys in the map
    const mapKeys = Array.from(mongoIdToTopicMap.keys());
    const mongoDbStyleKeys = mapKeys.filter(k => k.length === 24 && /^[a-f0-9]{24}$/.test(k));
    console.warn('[getTopicByMongoId] Topic not found for ID:', mongoId);
    console.warn('[getTopicByMongoId] mongoIdToTopicMap size:', mongoIdToTopicMap.size);
    console.warn('[getTopicByMongoId] MongoDB-style keys in map (first 10):', mongoDbStyleKeys.slice(0, 10));
    console.warn('[getTopicByMongoId] Looking for key exists?', mapKeys.includes(mongoId));
    console.warn('[getTopicByMongoId] Available FLAT_TOPICS ids:', FLAT_TOPICS.slice(0, 5).map(ft => ft.id));

    return null;
  };

  const handleTabSwitch = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const tabs: TabType[] = ['concepts', 'practice', 'ai-assistant'];
    const currentIndex = tabs.indexOf(activeTab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex !== currentIndex) {
      event.preventDefault();
      setActiveTab(tabs[nextIndex]);
      const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons?.[nextIndex]?.focus();
    }
  };

  const openEnrollModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    enrollTriggerRef.current = event.currentTarget;
    setShowEnrollModal(true);
  };

  const closeEnrollModal = () => {
    if (enrolling) return;
    setShowEnrollModal(false);
    setEnrollError('');
    requestAnimationFrame(() => enrollTriggerRef.current?.focus());
  };

  useEffect(() => {
    if (!showEnrollModal) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEnrollModal();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  });

  const handleEnrollCourse = async () => {
    if (!enrollCourseCode.trim()) return;

    try {
      setEnrolling(true);
      setEnrollError('');

      // First, get the course by code to find its ID
      const { course, isEnrolled } = await teacherApi.getCourseByCode(enrollCourseCode.trim());

      if (isEnrolled) {
        setEnrollError('您已经注册了这门课程');
        setEnrolling(false);
        return;
      }

      // Enroll in the course
      await teacherApi.enrollInCourse(course._id, enrollCourseCode.trim());

      // Refresh enrolled courses
      const courses = await teacherApi.getCourses({ enrolled: 'me' });
      setEnrolledCourses(courses);

      // Extract all topic IDs from enrolled courses (MongoDB _id values)
      // Also collect full topic objects from enrolled courses for topics not in database
      const mongoTopicIds = new Set<string>();
      const enrolledCourseTopics = new Map<string, any>(); // Store full topic objects from enrolled courses

      courses.forEach((enrolledCourse: Course) => {
        enrolledCourse.chapters.forEach((chapter: any) => {
          // Add topics directly in chapters (can be string IDs or objects)
          chapter.topics?.forEach((topicItem: any) => {
            const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
            mongoTopicIds.add(topicId);
            // If it's a full object, store it for later use
            if (typeof topicItem === 'object' && topicItem !== null && (topicItem.topicId || topicItem._id)) {
              enrolledCourseTopics.set(topicItem.topicId || topicItem._id, topicItem);
            }
          });
          // Add topics in sections (can be string IDs or objects)
          chapter.sections?.forEach((section: any) => {
            section.topics?.forEach((topicItem: any) => {
              const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
              mongoTopicIds.add(topicId);
              // If it's a full object, store it for later use
              if (typeof topicItem === 'object' && topicItem !== null && (topicItem.topicId || topicItem._id)) {
                enrolledCourseTopics.set(topicItem.topicId || topicItem._id, topicItem);
              }
            });
          });
        });
      });

      // Fetch all topics from database and update the mongoIdToTopicMap
      const { topics: allDbTopics } = await topicsApi.getAllTopics({ published: true });

      // Create a mapping from MongoDB _id to TopicContent
      const mongoIdToTopic = new Map<string, TopicContent>();

      // Add FLAT_TOPICS to the map
      FLAT_TOPICS.forEach(flatTopic => {
        const topicContent: TopicContent = {
          ...flatTopic,
          categoryCode: flatTopic.mainCategoryCode,
        };
        mongoIdToTopic.set(flatTopic.id, topicContent);
      });

      // Add database topics to the map (both FLAT_TOPICS matches and non-matches)
      allDbTopics.forEach((dbTopic: topicsApi.Topic) => {
        const flatTopic = FLAT_TOPICS.find(ft => ft.code === dbTopic.slug);
        if (flatTopic) {
          const topicContent: TopicContent = {
            ...flatTopic,
            _id: dbTopic._id,
            categoryCode: flatTopic.mainCategoryCode,
          };
          mongoIdToTopic.set(dbTopic._id, topicContent);
        } else {
          const topicContent: TopicContent = {
            id: dbTopic._id,
            _id: dbTopic._id,
            code: dbTopic.slug || dbTopic._id,
            title: dbTopic.title,
            chineseTitle: dbTopic.title,
            description: dbTopic.description || '',
            content: {
              definition: dbTopic.description || '',
              keyPoints: [],
              examples: [],
              applications: [],
              summary: dbTopic.description || '',
            },
            mainCategoryCode: dbTopic.category?.slug || 'uncategorized',
            mainCategoryName: dbTopic.category?.name || '未分类',
            subCategoryCode: '',
            subCategoryName: '',
            categoryCode: dbTopic.category?.slug || 'uncategorized',
          };
          mongoIdToTopic.set(dbTopic._id, topicContent);
        }
      });

      // IMPORTANT: Add topics from enrolled courses that aren't in the database
      // This handles cases where enrolled courses have topics with different IDs than what's in the database
      enrolledCourseTopics.forEach((courseTopic: any, topicId: string) => {
        // Only add if not already in the map (not found in database)
        if (!mongoIdToTopic.has(topicId)) {
          const topicContent: TopicContent = {
            id: topicId,
            _id: topicId,
            code: courseTopic.slug || courseTopic.code || topicId,
            title: courseTopic.title || courseTopic.chineseTitle || 'Unknown Topic',
            chineseTitle: courseTopic.title || courseTopic.chineseTitle || 'Unknown Topic',
            description: courseTopic.description || '',
            content: {
              definition: courseTopic.description || '',
              keyPoints: courseTopic.keyPoints || [],
              examples: courseTopic.examples || [],
              applications: courseTopic.applications || [],
              summary: courseTopic.summary || courseTopic.description || '',
            },
            mainCategoryCode: courseTopic.mainCategoryCode || courseTopic.category?.slug || 'uncategorized',
            mainCategoryName: courseTopic.mainCategoryName || courseTopic.category?.name || '未分类',
            subCategoryCode: courseTopic.subCategoryCode || '',
            subCategoryName: courseTopic.subCategoryName || '',
            categoryCode: courseTopic.categoryCode || courseTopic.category?.slug || 'uncategorized',
          };
          mongoIdToTopic.set(topicId, topicContent);
          console.log('[handleEnrollCourse] Created TopicContent from enrolled course topic:', topicId, courseTopic.title);
        }
      });

      setMongoIdToTopicMap(mongoIdToTopic);

      // Use MongoDB _id values directly for enrolledTopicIds
      const enrolledMongoTopicIds = new Set<string>(mongoTopicIds);
      setEnrolledTopicIds(enrolledMongoTopicIds);

      // Close modal and reset
      setShowEnrollModal(false);
      setEnrollCourseCode('');
      setEnrollError('');
      toast.success('课程注册成功');
      requestAnimationFrame(() => courseSectionTitleRef.current?.focus());
    } catch (error: any) {
      console.error('Failed to enroll in course:', error);
      if (error.response?.data?.message) {
        setEnrollError(error.response.data.message);
      } else if (error.response?.status === 404) {
        setEnrollError('未找到该课程代码，请检查后重试');
      } else {
        setEnrollError('注册课程失败，请稍后重试');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleTopicMouseEnter = (topic: TopicContent, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTopicTooltip({
      visible: true,
      topic,
      x: rect.right + 10,
      y: rect.top
    });
  };

  const handleTopicMouseLeave = () => {
    setTopicTooltip(prev => ({ ...prev, visible: false }));
  };

  // Helper function to extract teacher name from course
  const getTeacherName = (course: any): string => {
    if (typeof course.teacher === 'string') {
      return course.teacher;
    } else if (course.teacher && typeof course.teacher === 'object') {
      return course.teacher.name || course.teacher.email || 'Unknown';
    }
    return 'Unknown';
  };

  // Helper function to get all topic IDs from a course
  const getCourseTopicIds = (course: any): string[] => {
    const topicIds: string[] = [];

    course.chapters?.forEach((chapter: any) => {
      // Get direct topics from chapter
      chapter.topics?.forEach((topicId: string | any) => {
        const id = typeof topicId === 'object' && topicId !== null ? topicId._id || topicId.id : topicId;
        if (id) topicIds.push(id);
      });

      // Get topics from sections
      chapter.sections?.forEach((section: any) => {
        section.topics?.forEach((topicId: string | any) => {
          const id = typeof topicId === 'object' && topicId !== null ? topicId._id || topicId.id : topicId;
          if (id) topicIds.push(id);
        });
      });
    });

    return topicIds;
  };

  // Helper function to calculate course progress
  const getCourseProgress = (course: any): { completed: number; total: number; percent: number } => {
    const allTopicIds = getCourseTopicIds(course);
    const completedCount = allTopicIds.filter(id => completedTopics.has(id)).length;
    const totalCount = allTopicIds.length;
    const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return { completed: completedCount, total: totalCount, percent };
  };

  // Course tooltip handlers
  const handleCourseMouseEnter = (course: any, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    // Clear any existing timeout
    if (courseTooltipTimeoutRef.current) {
      clearTimeout(courseTooltipTimeoutRef.current);
    }

    setCourseTooltip({
      visible: true,
      course,
      x: rect.left,
      y: rect.top - 10
    });

    // Auto-hide after 10 seconds
    courseTooltipTimeoutRef.current = setTimeout(() => {
      setCourseTooltip(prev => ({ ...prev, visible: false }));
    }, 10000);
  };

  const handleCourseMouseLeave = () => {
    // Clear the timeout if mouse leaves
    if (courseTooltipTimeoutRef.current) {
      clearTimeout(courseTooltipTimeoutRef.current);
      courseTooltipTimeoutRef.current = null;
    }
    setCourseTooltip(prev => ({ ...prev, visible: false }));
  };

  // Cleanup course tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (courseTooltipTimeoutRef.current) {
        clearTimeout(courseTooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleTopicClick = async (topic: TopicContent, course?: any) => {
    if (hasFullTopicLearningAccess) {
      const topicIdentifier = topic._id || topic.code || topic.id;
      navigate(`/learning/topic/${encodeURIComponent(topicIdentifier)}`);
      return;
    }

    // Try to load topic from database to get htmlContent
    let topicToSet = topic;

    // Check if we already have it in cache
    if (dbTopics.has(topic.id)) {
      topicToSet = dbTopics.get(topic.id)!;
    } else {
      // Load from database
      const dbTopic = await loadTopicFromDatabase(topic);
      if (dbTopic) {
        // Cache it
        setDbTopics(prev => new Map(prev).set(topic.id, dbTopic));
        topicToSet = dbTopic;
      }
    }

    setSelectedTopic(topicToSet);
    setReadyTopicFrame('');
    setExplanationFocused(false);
    setDemonstrationFocused(false);
    setIsModalVisible(true);
    // Set course context if provided
    setLearningCourse(course || null);
    if (!course) {
      setCollapsedMainCategories(previous => {
        const next = new Set(previous);
        next.delete(topicToSet.mainCategoryCode);
        return next;
      });
      setCollapsedSubCategories(previous => {
        const next = new Set(previous);
        next.delete(topicToSet.subCategoryCode);
        return next;
      });
    }
    // Reset learning state when switching topics - overlay reappears
    setLearningStarted(false);
    setLearningStartTime(null);
    // Reset states when switching topics
    setSelfExplanation('');
    setSavedExplanation('');
    setShowSavedIndicator(false);
    setIsSidebarCollapsed(false);
    setCurrentQuestionIndex(0);
    setQuizAnswers([]);
    setShowQuizResult(false);
    setQuizScore(0);
    setConsecutiveCorrect(0);
    // Reset edit mode when switching topics
    setIsEditMode(false);
    setEditedContent('');
    setQuizPassed(false);

  };

  const handleStartCourseLearning = (course: any) => {
    // Open course learning page in a new window/tab
    const learningWindow = window.open(`/student/course/${course._id}`, '_blank');
    if (learningWindow) {
      recordLearningBoundary(
        'learning.course_accessed',
        { kind: 'course', id: String(course._id || '') }
      );
    }
  };

  // Helper functions for course navigation
  const getAllTopicsInCourse = (course: any): TopicContent[] => {
    const allTopics: TopicContent[] = [];
    course.chapters.forEach((chapter: any) => {
      // Topics directly in chapter
      chapter.topics?.forEach((topicItem: string | { topicId?: string; _id?: string }) => {
        const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
        if (topicId) {
          const topic = getTopicByMongoId(topicId);
          if (topic) allTopics.push(topic);
        }
      });
      // Topics in sections
      chapter.sections?.forEach((section: any) => {
        section.topics?.forEach((topicItem: string | { topicId?: string; _id?: string }) => {
          const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
          if (topicId) {
            const topic = getTopicByMongoId(topicId);
            if (topic) allTopics.push(topic);
          }
        });
      });
    });
    return allTopics;
  };

  const getCurrentTopicIndexInCourse = (): number => {
    if (!learningCourse || !selectedTopic) return -1;
    const allTopics = getAllTopicsInCourse(learningCourse);
    return allTopics.findIndex(t => t.id === selectedTopic.id);
  };

  const getPreviousTopicInCourse = (): TopicContent | null => {
    if (!learningCourse || !selectedTopic) return null;
    const allTopics = getAllTopicsInCourse(learningCourse);
    const currentIndex = allTopics.findIndex(t => t.id === selectedTopic.id);
    return currentIndex > 0 ? allTopics[currentIndex - 1] : null;
  };

  const getNextTopicInCourse = (): TopicContent | null => {
    if (!learningCourse || !selectedTopic) return null;
    const allTopics = getAllTopicsInCourse(learningCourse);
    const currentIndex = allTopics.findIndex(t => t.id === selectedTopic.id);
    return currentIndex >= 0 && currentIndex < allTopics.length - 1 ? allTopics[currentIndex + 1] : null;
  };

  const handlePreviousTopic = async () => {
    const prevTopic = getPreviousTopicInCourse();
    if (prevTopic) {
      await handleTopicClick(prevTopic, learningCourse);
    }
  };

  const handleNextTopic = async () => {
    const nextTopic = getNextTopicInCourse();
    if (nextTopic) {
      await handleTopicClick(nextTopic, learningCourse);
    }
  };

  const handleStartLearning = () => {
    const startTime = Date.now();
    setLearningStartTime(startTime);
    setLearningStarted(true);
    const targetId = selectedTopic?._id || selectedTopic?.id;
    if (targetId && OBJECT_ID_PATTERN.test(targetId)) {
      const type = viewedTopicIdsRef.current.has(targetId)
        ? 'learning.topic_revisited'
        : 'learning.content_viewed';
      viewedTopicIdsRef.current.add(targetId);
      recordLearningBoundary(type, { kind: 'topic', id: targetId });
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsQuizModalVisible(false);
    setTimeout(() => {
      setSelectedTopic(null);
      setSelfExplanation('');
      setSavedExplanation('');
    }, 300);
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleToggleFullscreen = () => {
    setIsContentFullscreen(!isContentFullscreen);
  };

  // Toggle topic completion status (called by learning status switch)
  const toggleTopicCompletion = async (topicId: string) => {
    const isCompleting = !completedTopics.has(topicId);
    const courseId = learningCourse?._id || learningCourse?.id;
    await persistTopicCompletion({
      topicId,
      courseId,
      isCompleting,
      setTopics: setCompletedTopics,
      notifyFailure: () => toast.error('无法更新学习状态，请重试')
    });
  };

  // Check if user can edit content (content manager or higher)
  const canEditContent = user ? userIsAdmin(user) : false;

  // Convert topic content to HTML for Tiptap editor
  const convertContentToHTML = (topic: TopicContent): string => {
    if (!topic.content) return '';

    const keywords = topic.keywords || [];

    const highlightKeywords = (text: string): string => {
      if (!keywords.length) return text;
      let result = text;
      keywords.forEach(kw => {
        const regex = new RegExp(`\\b${kw.term}\\b`, 'gi');
        result = result.replace(regex, `<mark style="background: rgba(139, 92, 246, 0.3); padding: 0.1em 0.2em; border-radius: 3px;">${kw.term}</mark>`);
      });
      return result;
    };

    let html = '';

    if (topic.content.definition) {
      html += `<h2>📖 定义</h2>\n<p>${highlightKeywords(topic.content.definition)}</p>\n`;
    }

    if (topic.content.keyPoints?.length) {
      html += `<h2>🎯 核心要点</h2>\n<ul>${topic.content.keyPoints.map(p => `<li>${highlightKeywords(p)}</li>`).join('')}</ul>\n`;
    }

    if (topic.content.formulas?.length) {
      html += `<h2>📐 公式</h2>\n`;
      topic.content.formulas.forEach(f => {
        html += `<div style="background: rgba(0,0,0,0.3); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">\n`;
        html += `<div style="font-size: 1.1rem; margin-bottom: 0.5rem;">${f.formula}</div>\n`;
        html += `<div style="opacity: 0.8;">${highlightKeywords(f.explanation)}</div>\n`;
        html += `</div>\n`;
      });
    }

    if (topic.content.examples?.length) {
      html += `<h2>💡 实例</h2>\n`;
      topic.content.examples.forEach(ex => {
        html += `<div style="background: rgba(139, 92, 246, 0.1); border-left: 4px solid #8b5cf6; padding: 1rem; margin-bottom: 1rem; border-radius: 8px;">\n`;
        html += `<strong>${highlightKeywords(ex.title)}</strong>\n`;
        html += `<p>${highlightKeywords(ex.content)}</p>\n`;
        html += `</div>\n`;
      });
    }

    if (topic.content.applications?.length) {
      html += `<h2>🔧 应用</h2>\n<ul>${topic.content.applications.map(a => `<li>${highlightKeywords(a)}</li>`).join('')}</ul>\n`;
    }

    if (topic.content.summary) {
      html += `<div style="background: rgba(74, 222, 128, 0.1); border-left: 4px solid #4ade80; padding: 1.5rem; border-radius: 8px; margin-top: 1rem;">\n`;
      html += `<strong>📝 总结</strong>\n`;
      html += `<p>${highlightKeywords(topic.content.summary)}</p>\n`;
      html += `</div>\n`;
    }

    return html;
  };

  // Clean up HTML content that was saved with <pre><code> wrapper
  const cleanHtmlContent = (html: string): string => {
    // Check if content is wrapped in <pre><code> tags (Tiptap's default for code blocks)
    const preCodeMatch = html.match(/^<pre><code>([\s\S]*?)<\/code><\/pre>$/);
    if (preCodeMatch) {
      // Extract the content and decode HTML entities
      let content = preCodeMatch[1];
      // Decode common HTML entities
      content = content
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      return content;
    }

    // Also decode common HTML entities for non-wrapped content
    // This fixes issues with parentheses and other characters being encoded
    let content = html;
    content = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#40;/g, '(')   // Decode left parenthesis
      .replace(/&#41;/g, ')')   // Decode right parenthesis
      .replace(/&lparen;/g, '(')
      .replace(/&rparen;/g, ')');
    return content;
  };

  // Render LaTeX equations in HTML content using KaTeX
  const renderLatexEquations = (html: string): string => {
    // First, render block math equations (delimited by $$...$$)
    let processed = html.replace(/\$\$([^\$]+)\$\$/g, (match, latex) => {
      try {
        return renderToString(latex.trim(), {
          displayMode: true,
          throwOnError: false,
          strict: false,
          trust: false,
        });
      } catch (error) {
        console.error('Error rendering block math:', error);
        return match; // Return original if rendering fails
      }
    });

    // Then, render inline math equations (delimited by $...$)
    // Only match if the content contains a backslash (indicating LaTeX command)
    // This prevents regular text with parentheses from being treated as math
    processed = processed.replace(/\$([^\$\n\\]*\\[^\$\n]*?|[^\$\n]+?)\$/g, (match, latex) => {
      // Only render if it contains a backslash (LaTeX command) or common math symbols
      if (latex.includes('\\') || latex.includes('_') || latex.includes('^') || latex.includes('{')) {
        try {
          return renderToString(latex.trim(), {
            displayMode: false,
            throwOnError: false,
            strict: false,
            trust: false,
          });
        } catch (error) {
          console.error('Error rendering inline math:', error);
          return match; // Return original if rendering fails
        }
      }
      return match; // Return original if it doesn't look like LaTeX
    });

    return processed;
  };

  // Load topic from database to get htmlContent
  const loadTopicFromDatabase = async (topic: TopicContent): Promise<TopicContent | null> => {
    try {
      const API_BASE = (process.env as any).REACT_APP_API_URL || 'http://localhost:5000/api';

      // First, try to find by code/slug (since Excel topics use codes like 'GD1', 'DM1', etc.)
      const response = await fetch(`${API_BASE}/topics/slug/${topic.code}`);
      if (!response.ok) {
        // Topic doesn't exist in database yet, return null
        return null;
      }

      const data = await response.json();
      const dbTopic = data.data.topic;

      // Extract the latest version from learningContent array
      let latestHtmlContent = dbTopic.htmlContent; // Fallback to old htmlContent field
      let contentRevision: string | undefined;
      if (dbTopic.learningContent && Array.isArray(dbTopic.learningContent) && dbTopic.learningContent.length > 0) {
        const latestVersion = dbTopic.learningContent.find((v: any) => v.isLatest === true);
        if (latestVersion && latestVersion.htmlContent) {
          latestHtmlContent = latestVersion.htmlContent;
          contentRevision = latestVersion._id;
        }
      }

      // Convert database topic to TopicContent format, preserving Excel topic fields
      return {
        ...topic, // Keep all Excel topic fields
        _id: dbTopic._id, // Store MongoDB _id for API calls
        content: dbTopic.content,
        htmlContent: latestHtmlContent,
        contentRevision,
        keywords: dbTopic.keywords || topic.keywords,
        quiz: dbTopic.exercises?.filter((e: any) => e.type === 'quiz').map((q: any) => q.content) || topic.quiz,
      };
    } catch (error) {
      console.error('Error loading topic from database:', error);
      return null;
    }
  };

  // Handle entering edit mode
  const handleEnterEditMode = () => {
    if (!selectedTopic) return;

    // Use existing htmlContent if available, otherwise convert from structured content
    let html = '';
    if (selectedTopic.htmlContent) {
      // Clean up the HTML content before editing
      html = cleanHtmlContent(selectedTopic.htmlContent);
    } else if (selectedTopic.content) {
      html = convertContentToHTML(selectedTopic);
    }

    setEditedContent(html);
    setIsEditMode(true);
  };

  // Handle canceling edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedContent('');
  };

  // Handle saving edited content
  const handleSaveContent = async () => {
    if (!selectedTopic) return;

    setIsSaving(true);
    try {
      const API_BASE = (process.env as any).REACT_APP_API_URL || 'http://localhost:5000/api';


      // Call API to save content
      const response = await fetch(`${API_BASE}/topics/${selectedTopic._id || selectedTopic.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ htmlContent: editedContent })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Save failed with status:', response.status, errorData);
        throw new Error(errorData.message || 'Failed to save content');
      }

      const data = await response.json();
      console.log('Save successful:', data);

      // Update the selected topic with the new HTML content from response
      const updatedTopic = {
        ...selectedTopic,
        htmlContent: editedContent,
        ...data.data.topic // Merge any other updated fields from backend
      };

      // Update the topic in TOPICS_DATA
      const topicIndex = TOPICS_DATA.findIndex(t => t.id === selectedTopic.id);
      if (topicIndex !== -1) {
        TOPICS_DATA[topicIndex] = updatedTopic;
      }

      // Update selectedTopic state
      setSelectedTopic(updatedTopic);

      toast.success('内容已保存');
      setIsEditMode(false);
    } catch (error) {
      console.error('Save content error:', error);
      toast.error('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle main category collapse
  const toggleMainCategory = (categoryCode: string) => {
    setCollapsedMainCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryCode)) {
        newSet.delete(categoryCode);
      } else {
        newSet.add(categoryCode);
      }
      return newSet;
    });
  };

  // Toggle subcategory collapse
  const toggleSubCategory = (subCategoryCode: string) => {
    setCollapsedSubCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subCategoryCode)) {
        newSet.delete(subCategoryCode);
      } else {
        newSet.add(subCategoryCode);
      }
      return newSet;
    });
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterId: string) => {
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleTopicSelect = async (topicId: string) => {
    let topic = TOPICS_DATA.find(t => t.id === topicId);
    if (!topic) return;

    // Try to enrich with _id from mongoIdToTopicMap
    const enrichedTopic = mongoIdToTopicMap.get(topicId);
    if (enrichedTopic && enrichedTopic._id) {
      topic = { ...topic, _id: enrichedTopic._id };
    }

    setSelectedTopic(topic);
    // Reset learning state - overlay reappears when switching topics
    setLearningStarted(false);
    setLearningStartTime(null);
    // Scroll to top of content
    const contentArea = document.querySelector('[data-content-area="true"]');
    contentArea?.scrollTo({ top: 0, behavior: 'smooth' });
    // Reset quiz and explanation states
    setSelfExplanation('');
    setSavedExplanation('');
    setCurrentQuestionIndex(0);
    setQuizAnswers([]);
    setShowQuizResult(false);
    setQuizScore(0);
  };

  const handleSaveExplanation = () => {
    if (selfExplanation.trim()) {
      setSavedExplanation(selfExplanation);
      setShowSavedIndicator(true);
      setTimeout(() => setShowSavedIndicator(false), 2000);
    }
  };

  // Quiz handlers
  const handleStartQuiz = () => {
    if (selectedTopic?.quiz && selectedTopic.quiz.length > 0) {
      setCurrentQuestionIndex(0);
      setQuizAnswers(new Array(selectedTopic.quiz.length).fill(null));
      setShowQuizResult(false);
      setQuizScore(0);
      setIsQuizModalVisible(true);
    }
  };

  const handleSelectAnswer = (questionIndex: number, answerIndex: number) => {
    const newAnswers = [...quizAnswers];
    newAnswers[questionIndex] = answerIndex;
    setQuizAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    if (selectedTopic?.quiz && currentQuestionIndex < selectedTopic.quiz.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!selectedTopic?.quiz) return;

    // Calculate score, consecutive correct, and prepare answer data
    let correct = 0;
    let consecutive = 0;
    let maxConsecutive = 0;
    const answers: Array<{ question: string; correct: boolean }> = [];

    selectedTopic.quiz.forEach((question, index) => {
      const isCorrect = quizAnswers[index] === question.correctAnswer;
      if (isCorrect) {
        correct++;
        consecutive++;
        maxConsecutive = Math.max(maxConsecutive, consecutive);
      } else {
        consecutive = 0;
      }
      answers.push({
        question: question.question,
        correct: isCorrect
      });
    });

    setQuizScore(correct);
    setConsecutiveCorrect(maxConsecutive);
    setShowQuizResult(true);

    // Submit to API
    try {
      if (token) {
        const response = await fetch(`${(process.env as any).REACT_APP_API_URL || 'http://localhost:5000'}/api/topics/${selectedTopic._id || selectedTopic.id}/quiz`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            quizId: selectedTopic.id,
            score: correct,
            totalQuestions: selectedTopic.quiz.length,
            answers: answers
          })
        });

        const data = await response.json();
        if (data.success) {
          console.log('Quiz submitted successfully:', data);
          setQuizSubmitResult(data.data);
          setQuizPassed(data.data.passed);
        }
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
    }
  };

  const handleRetryQuiz = () => {
    setCurrentQuestionIndex(0);
    setQuizAnswers([]);
    setShowQuizResult(false);
    setQuizScore(0);
    setConsecutiveCorrect(0);
    setQuizPassed(false);
    setQuizSubmitResult(null);
  };

  const handleCloseQuizModal = () => {
    setIsQuizModalVisible(false);
  };

  // Keyword handlers
  const handleKeywordClick = (keyword: Keyword, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setKeywordTooltip({
      visible: true,
      keyword,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    const topicId = selectedTopic?._id || selectedTopic?.id;
    if (topicId) {
      recordLearningBoundary('discovery.keyword_opened', { kind: 'topic', id: topicId });
    }
  };

  const handleKeywordTooltipClose = () => {
    setKeywordTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleGenerateAIExplanation = async (keyword: Keyword) => {
    if (!selectedTopic) return;

    // 显示加载状态
    setKeywordTooltip(prev => ({
      ...prev,
      keyword: {
        ...keyword,
        aiExplanation: '正在生成AI解释...'
      }
    }));

    try {
      const API_BASE = (process.env as any).REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_BASE}/ai/keyword-explanation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword: keyword.term,
          definition: keyword.definition,
          context: selectedTopic.title
        })
      });

      const data = await response.json();

      if (data.success && data.data?.explanation) {
        // 更新关键词的AI解释
        setKeywordTooltip(prev => ({
          ...prev,
          keyword: {
            ...prev.keyword!,
            aiExplanation: data.data.explanation
          }
        }));

        // 更新keywords数据中的AI解释（实际应用中应该保存到数据库）
        const topicIndex = TOPICS_DATA.findIndex(t => t.id === selectedTopic.id);
        if (topicIndex !== -1 && TOPICS_DATA[topicIndex].keywords) {
          const keywordIndex = TOPICS_DATA[topicIndex].keywords!.findIndex(k => k.term === keyword.term);
          if (keywordIndex !== -1) {
            TOPICS_DATA[topicIndex].keywords![keywordIndex].aiExplanation = data.data.explanation;
          }
        }
      }
    } catch (error) {
      console.error('Failed to generate AI explanation:', error);
      setKeywordTooltip(prev => ({
        ...prev,
        keyword: {
          ...prev.keyword!,
          aiExplanation: '生成失败，请稍后重试'
        }
      }));
    }
  };

  const handleGenerateDemoCode = async (language: string = 'R') => {
    if (!selectedTopic) return;

    setIsGeneratingDemoCode(true);

    try {
      const API_BASE = (process.env as any).REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/ai/demo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic: selectedTopic.title,
          language
        })
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Update the topic with demo code
        const topicIndex = TOPICS_DATA.findIndex(t => t.id === selectedTopic.id);
        if (topicIndex !== -1) {
          TOPICS_DATA[topicIndex].demoCode = {
            code: data.data.code,
            explanation: data.data.explanation,
            language
          };
          // Force re-render
          setSelectedTopic({ ...TOPICS_DATA[topicIndex] });
        }
      } else {
        console.error('Failed to generate demo code:', data.message);
      }
    } catch (error) {
      console.error('Failed to generate demo code:', error);
    } finally {
      setIsGeneratingDemoCode(false);
    }
  };

  const highlightKeywords = (text: string, keywords: Keyword[]): React.ReactNode => {
    if (!keywords || keywords.length === 0) return text;

    let result: React.ReactNode[] = [];
    let lastIndex = 0;

    // Sort keywords by length (longest first) to handle overlapping terms
    const sortedKeywords = [...keywords].sort((a, b) => b.term.length - a.term.length);

    // Create a map of matches
    const matches: Array<{ start: number; end: number; keyword: Keyword }> = [];

    sortedKeywords.forEach(keyword => {
      const regex = new RegExp(keyword.term, 'gi');
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        // Check if this match overlaps with any existing match
        const overlaps = matches.some(m =>
          (match!.index >= m.start && match!.index < m.end) ||
          (match!.index + match![0].length > m.start && match!.index + match![0].length <= m.end) ||
          (match!.index <= m.start && match!.index + match![0].length >= m.end)
        );
        if (!overlaps) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            keyword
          });
        }
      }
    });

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Build the result
    matches.forEach((matchItem, index) => {
      // Add text before the match
      if (matchItem.start > lastIndex) {
        result.push(text.slice(lastIndex, matchItem.start));
      }

      // Add the highlighted keyword
      result.push(
        <KeywordHighlight
          key={`keyword-${index}`}
          $importance={matchItem.keyword.importance}
          onClick={(e) => handleKeywordClick(matchItem.keyword, e)}
        >
          {text.slice(matchItem.start, matchItem.end)}
        </KeywordHighlight>
      );

      lastIndex = matchItem.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result.length > 0 ? result : text;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isQuizModalVisible) {
        handleCloseQuizModal();
      } else if (isModalVisible) {
        handleCloseModal();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isQuizModalVisible, isModalVisible]);

  const renderTopicContent = (topic: TopicContent) => {
    const isCompleted = topic._id ? completedTopics.has(topic._id) : false;
    return (
      <TopicCard
        key={topic.id}
        onClick={() => handleTopicClick(topic)}
        style={{ position: 'relative' }}
      >
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <CompletionCheckbox
            $completed={isCompleted}
            aria-label={isCompleted ? '已学习' : '未学习'}
          >
            {isCompleted ? '✓' : ''}
          </CompletionCheckbox>
          <div style={{ flex: 1 }}>
            <TopicHeader>
              <TopicTitle>{topic.chineseTitle || topic.title}</TopicTitle>
            </TopicHeader>
            <TopicDescription>{topic.description}</TopicDescription>
          </div>
        </div>
      </TopicCard>
    );
  };

  const renderModalContent = (topic: TopicContent) => {
    const keywords = topic.keywords || [];

    // If in edit mode, show Tiptap editor
    if (isEditMode && canEditContent) {
      return (
        <TiptapEditor
          content={editedContent}
          onChange={setEditedContent}
          placeholder="开始编辑主题内容..."
          editable={true}
        />
      );
    }

    // If htmlContent exists (saved from Tiptap), render it
    if (topic.htmlContent) {
      // Clean up HTML content (remove <pre><code> wrapper if present and decode entities)
      // Clean up HTML content (remove <pre><code> wrapper if present and decode entities)
      const cleanedHtml = cleanHtmlContent(topic.htmlContent);

      // Render LaTeX equations using KaTeX
      const htmlWithEquations = renderLatexEquations(cleanedHtml);

      // Check if the HTML contains script or style tags that need iframe rendering
      const hasScriptOrStyle = /<(script|style)[\s>]/i.test(cleanedHtml);

      if (hasScriptOrStyle) {
        // Use iframe for content with scripts or styles
        return <SandboxedHTMLContent key={`${topic._id}/${topic.contentRevision || 'unversioned'}`} html={htmlWithEquations} />;
      }

      // Use simple HTML rendering for content without scripts/styles
      return (
        <HTMLContent
          dangerouslySetInnerHTML={{ __html: htmlWithEquations }}
        />
      );
    }

    // If content is not available, show placeholder
    if (!topic.content) {
      return (
        <ContentPlaceholder>
          <p>该主题内容正在建设中...</p>
          <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>敬请期待更多精彩内容！</p>
        </ContentPlaceholder>
      );
    }

    return (
      <>
        <SectionTitle>📖 定义</SectionTitle>
        <Definition>{highlightKeywords(topic.content.definition, keywords)}</Definition>

        <SectionTitle>🎯 核心要点</SectionTitle>
        <KeyPoints>
          {topic.content.keyPoints.map((point, idx) => (
            <li key={idx}>{highlightKeywords(point, keywords)}</li>
          ))}
        </KeyPoints>

        {topic.content.formulas && topic.content.formulas.length > 0 && (
          <ModalSection>
            <SectionTitle>📐 公式</SectionTitle>
            {topic.content.formulas.map((formula, idx) => (
              <FormulaBox key={idx}>
                <div className="formula">
                  <InlineMath math={formula.formula} />
                </div>
                <div className="explanation">{highlightKeywords(formula.explanation, keywords)}</div>
              </FormulaBox>
            ))}
          </ModalSection>
        )}

        <ModalSection>
          <SectionTitle>💡 实例</SectionTitle>
          {topic.content.examples.map((example, idx) => (
            <ExampleBox key={idx}>
              <div className="title">{highlightKeywords(example.title, keywords)}</div>
              <div className="content">{highlightKeywords(example.content, keywords)}</div>
            </ExampleBox>
          ))}
        </ModalSection>

        <ModalSection>
          <SectionTitle>🔧 应用</SectionTitle>
          <ApplicationsList>
            {topic.content.applications.map((app, idx) => (
              <li key={idx}>{highlightKeywords(app, keywords)}</li>
            ))}
          </ApplicationsList>
        </ModalSection>

        <ModalSection>
          <SummaryBox>
            <div className="title">📝 总结</div>
            <div className="content">{highlightKeywords(topic.content.summary, keywords)}</div>
          </SummaryBox>
        </ModalSection>

        {keywords && keywords.length > 0 && (
          <KeywordsListSection>
            <KeywordsListHeader>
              <span>🔑</span>
              <span>关键词</span>
            </KeywordsListHeader>
            <KeywordsGrid>
              {keywords.map((keyword, idx) => (
                <KeywordChip
                  key={idx}
                  $importance={keyword.importance}
                  onClick={(e) => handleKeywordClick(keyword, e)}
                >
                  {keyword.term}
                </KeywordChip>
              ))}
            </KeywordsGrid>
          </KeywordsListSection>
        )}

        {/* Demo Code Section */}
        {topic.demoCode && (
          <DemoCodeSection tabIndex={0} onFocusCapture={() => setDemonstrationFocused(true)}
            onPointerDown={() => setDemonstrationFocused(true)}
            onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDemonstrationFocused(false); }}>
            <DemoCodeHeader>
              <DemoCodeTitle>
                <span>📊</span>
                <span>演示代码 ({topic.demoCode.language})</span>
              </DemoCodeTitle>
              <DemoCodeButtons>
                <DemoCodeLanguageButton
                  $active={topic.demoCode.language === 'R'}
                  onClick={() => handleGenerateDemoCode('R')}
                  disabled={isGeneratingDemoCode}
                >
                  R
                </DemoCodeLanguageButton>
                <DemoCodeLanguageButton
                  $active={topic.demoCode.language === 'Python'}
                  onClick={() => handleGenerateDemoCode('Python')}
                  disabled={isGeneratingDemoCode}
                >
                  Python
                </DemoCodeLanguageButton>
                <GenerateDemoCodeButton
                  onClick={() => topic.demoCode && handleGenerateDemoCode(topic.demoCode.language)}
                  disabled={isGeneratingDemoCode}
                  $loading={isGeneratingDemoCode}
                >
                  <span>{isGeneratingDemoCode ? '⏳' : '🔄'}</span>
                  <span>{isGeneratingDemoCode ? '生成中...' : '重新生成'}</span>
                </GenerateDemoCodeButton>
              </DemoCodeButtons>
            </DemoCodeHeader>
            <DemoCodeContent>
              <DemoCodeBlock>
                <code>{topic.demoCode.code}</code>
              </DemoCodeBlock>
            </DemoCodeContent>
            {topic.demoCode.explanation && (
              <DemoCodeExplanation>
                <strong>代码说明：</strong>
                {topic.demoCode.explanation}
              </DemoCodeExplanation>
            )}
          </DemoCodeSection>
        )}

        {/* Generate Demo Code Button when no demo code exists */}
        {!topic.demoCode && (
          <DemoCodeSection>
            <DemoCodeHeader>
              <DemoCodeTitle>
                <span>📊</span>
                <span>演示代码</span>
              </DemoCodeTitle>
              <DemoCodeButtons>
                <GenerateDemoCodeButton
                  onClick={() => handleGenerateDemoCode('R')}
                  disabled={isGeneratingDemoCode}
                  $loading={isGeneratingDemoCode}
                >
                  <span>{isGeneratingDemoCode ? '⏳' : '🤖'}</span>
                  <span>{isGeneratingDemoCode ? '生成中...' : '生成R代码'}</span>
                </GenerateDemoCodeButton>
                <GenerateDemoCodeButton
                  onClick={() => handleGenerateDemoCode('Python')}
                  disabled={isGeneratingDemoCode}
                  $loading={isGeneratingDemoCode}
                >
                  <span>{isGeneratingDemoCode ? '⏳' : '🤖'}</span>
                  <span>{isGeneratingDemoCode ? '生成中...' : '生成Python代码'}</span>
                </GenerateDemoCodeButton>
              </DemoCodeButtons>
            </DemoCodeHeader>
          </DemoCodeSection>
        )}
      </>
    );
  };

  const handleRunCode = () => {
    // Code runs and results show in the OutputPanel below the editor
    // No tab switch needed - plots display directly in output
  };

  const handleAskAI = () => {
    // 切换到统计助教页面
    setActiveTab('ai-assistant');
  };

  return (
    <DashboardLayout
      title="掌握应用统计学的艺术"
      subtitle={`欢迎回来，${user?.studentInfo?.realName || user?.name}！通过R语言实践和AI辅助，深入理解统计学原理与应用`}
      compact
    >
      <Container>
        {/* 主要内容区 */}
        <GlassCard>
          {/* Tab Navigation */}
          <TabNav role="tablist" aria-label="学习平台功能">
            <TabButton
              id="learning-tab-courses"
              role="tab"
              active={activeTab === 'concepts'}
              aria-selected={activeTab === 'concepts'}
              aria-controls="learning-panel-courses"
              tabIndex={activeTab === 'concepts' ? 0 : -1}
              onClick={() => handleTabSwitch('concepts')}
              onKeyDown={handleTabKeyDown}
            >
              📚 我的课程
            </TabButton>
            <TabButton
              id="learning-tab-practice"
              role="tab"
              active={activeTab === 'practice'}
              aria-selected={activeTab === 'practice'}
              aria-controls="learning-panel-practice"
              tabIndex={activeTab === 'practice' ? 0 : -1}
              onClick={() => handleTabSwitch('practice')}
              onKeyDown={handleTabKeyDown}
            >
              💻 R编程实践
            </TabButton>
            <TabButton
              id="learning-tab-ai"
              role="tab"
              active={activeTab === 'ai-assistant'}
              aria-selected={activeTab === 'ai-assistant'}
              aria-controls="learning-panel-ai"
              tabIndex={activeTab === 'ai-assistant' ? 0 : -1}
              onClick={() => handleTabSwitch('ai-assistant')}
              onKeyDown={handleTabKeyDown}
            >
              🎓 统计助教
            </TabButton>
          </TabNav>

          {/* Tab Contents */}
          <TabContent
            id="learning-panel-courses"
            role="tabpanel"
            aria-labelledby="learning-tab-courses"
            active={activeTab === 'concepts'}
            $isPractice={false}
          >
            <CourseSectionHeader>
              <CourseSectionTitleRow>
                <CourseSectionTitle ref={courseSectionTitleRef} tabIndex={-1}>
                  {isStudentView ? '📚 我的课程' : '📚 统计学学习主题'}
                </CourseSectionTitle>
                {isStudentView && (
                  <EnrollCourseButton onClick={openEnrollModal}>
                    📝 注册课程
                  </EnrollCourseButton>
                )}
              </CourseSectionTitleRow>
                <CourseSectionDescription>
                  {loadingCourses
                    ? '正在加载课程…'
                    : isStudentView
                      ? `您已注册了 ${enrolledCourses.length} 个课程`
                      : `悬停查看主题描述，点击查看详细内容 (${TOPICS_DATA.length} 个主题)`
                  }
                </CourseSectionDescription>
            </CourseSectionHeader>

            {isStudentView && loadingCourses && (
              <CoursesGrid aria-busy="true" aria-label="正在加载课程">
                {[0, 1, 2].map(item => <CourseSkeleton key={item} aria-hidden="true" />)}
              </CoursesGrid>
            )}

            {isStudentView && courseLoadError && !loadingCourses && (
              <CourseStatus role="alert">
                <p>课程加载失败，请稍后重试。</p>
                <EnrollCourseButton onClick={() => setCourseReloadKey(key => key + 1)}>
                  重新加载
                </EnrollCourseButton>
              </CourseStatus>
            )}

            {isStudentView && enrolledCourses.length === 0 && !loadingCourses && !courseLoadError && (
              <EmptyState>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
                <div style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>还没有注册任何课程</div>
                <div style={{ color: glassTheme.colors.text.secondary, marginBottom: '1.5rem' }}>
                  请输入教师提供的课程代码，注册后即可在此开始学习
                </div>
                <EnrollCourseButton onClick={openEnrollModal}>
                  📝 立即注册课程
                </EnrollCourseButton>
              </EmptyState>
            )}

            {/* Student view: Show enrolled courses with chapter/section structure */}
            {isStudentView && !loadingCourses && !courseLoadError && enrolledCourses.length > 0 && (
              <CoursesGrid role="list">
                {enrolledCourses.map((course) => {
                  const createdAt = course.createdAt ? new Date(course.createdAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }) : '未知';
                  const sectionCount = course.chapters.reduce((sum, ch) => sum + (ch.sections?.length || 0), 0);

                  // Generate gradient based on course index for visual variety
                  const gradients = [
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                  ];
                  const gradient = gradients[enrolledCourses.indexOf(course) % gradients.length];

                  // Calculate actual progress based on completed topics
                  const progress = getCourseProgress(course);
                  const completedTopicsInCourse = progress.completed;
                  const progressPercent = progress.percent;

                  return (
                    <CourseCard key={course._id} $accent={gradient} role="listitem">
                      <CourseHeader>
                        <CourseIcon $color={gradient}>📚</CourseIcon>
                        <CourseTitle>
                          <CourseName>
                            {course.title}
                            {course.description && (
                              <CourseDescInfoIcon
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setHoveredCourseTooltip({
                                    courseId: course._id,
                                    rect: {
                                      top: rect.top,
                                      left: rect.left,
                                      width: rect.width,
                                      height: rect.height
                                    }
                                  });
                                }}
                                onMouseLeave={() => {
                                  setHoveredCourseTooltip(null);
                                }}
                                onFocus={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setHoveredCourseTooltip({
                                    courseId: course._id,
                                    rect: {
                                      top: rect.top,
                                      left: rect.left,
                                      width: rect.width,
                                      height: rect.height
                                    }
                                  });
                                }}
                                onBlur={() => setHoveredCourseTooltip(null)}
                                onClick={() => {
                                  setSelectedCourseDesc({ title: course.title, description: course.description });
                                  setShowCourseDescModal(true);
                                }}
                                 aria-label={`查看《${course.title}》课程介绍`}
                              >
                                📖
                              </CourseDescInfoIcon>
                            )}
                          </CourseName>
                          <CourseCode>{course.courseCode}</CourseCode>
                        </CourseTitle>
                      </CourseHeader>

                      {false && course.description && (
                        <CourseDescription>{course.description}</CourseDescription>
                      )}

                      <CourseProgress>
                        <CourseProgressBar $progress={progressPercent} />
                        <CourseProgressLabel>
                          <span>学习进度</span>
                          <span>{progressPercent}% ({completedTopicsInCourse}/{progress.total})</span>
                        </CourseProgressLabel>
                      </CourseProgress>

                      <CourseMeta>
                        <CourseMetaItem>
                          <i className="fas fa-chalkboard-teacher"></i>
                          <span>教师：{getTeacherName(course)}</span>
                        </CourseMetaItem>
                        <CourseMetaItem>
                          <i className="far fa-calendar-alt"></i>
                          <span>创建时间：{createdAt}</span>
                        </CourseMetaItem>
                        <CourseMetaItem>
                          <i className="fas fa-book"></i>
                          <span>章节：{course.chapters.length} 章{sectionCount > 0 ? ` ${sectionCount} 节` : ''}</span>
                        </CourseMetaItem>
                        <CourseMetaItem>
                          <i className="fas fa-lightbulb"></i>
                          <span>知识元：{progress.total} 个</span>
                        </CourseMetaItem>
                      </CourseMeta>

                      <CourseStartButton onClick={() => handleStartCourseLearning(course)}>
                       
                        <span>进入学习</span>
                      </CourseStartButton>
                    </CourseCard>
                  );
                })}
              </CoursesGrid>
            )}

            {/* Teacher/Admin view: Show all topics by category */}
            {!isStudentView && !loadingCourses && enrolledTopicIds.size > 0 && catalogCategories.map((mainCategory) => {
              const isMainCollapsed = collapsedMainCategories.has(mainCategory.code);
              // Filter subcategories to only include those with enrolled topics
              const enrolledSubCategories = mainCategory.subcategories.filter(subcategory =>
                catalogTopics.some(t => t.subCategoryCode === subcategory.code && enrolledTopicIds.has(t.id))
              );
              const enrolledTopicsCount = enrolledSubCategories.reduce((sum, sub) =>
                sum + catalogTopics.filter(t => t.subCategoryCode === sub.code && enrolledTopicIds.has(t.id)).length, 0
              );

              if (enrolledTopicsCount === 0) return null;

              return (
                <TopicSection key={mainCategory.code}>
                  <MainCategoryHeader
                    $color={mainCategory.color}
                    onClick={() => toggleMainCategory(mainCategory.code)}
                  >
                    <CollapseIcon $collapsed={isMainCollapsed}>▼</CollapseIcon>
                    <span>{mainCategory.icon}</span>
                    <span>{mainCategory.chinese_name}</span>
                    <span style={{ fontSize: '0.8rem', opacity: 0.7, fontWeight: 400 }}>
                      ({enrolledTopicsCount}/{mainCategory.topics_count}个主题)
                    </span>
                  </MainCategoryHeader>

                  {!isMainCollapsed && enrolledSubCategories.map((subcategory) => {
                    const subCategoryTopics = catalogTopics.filter(t =>
                      t.subCategoryCode === subcategory.code && enrolledTopicIds.has(t.id)
                    ).map(topic => {
                      // Enrich with _id from mongoIdToTopicMap
                      const enriched = mongoIdToTopicMap.get(topic.id);
                      return enriched && enriched._id ? { ...topic, _id: enriched._id } : topic;
                    });
                    if (subCategoryTopics.length === 0) return null;

                    const isSubCollapsed = collapsedSubCategories.has(subcategory.code);
                    return (
                      <div key={subcategory.code}>
                        <SubCategoryHeader
                          $color={mainCategory.color}
                          onClick={() => toggleSubCategory(subcategory.code)}
                        >
                          <CollapseIcon $collapsed={isSubCollapsed}>▼</CollapseIcon>
                          {subcategory.chinese_name}
                        </SubCategoryHeader>
                        <TopicGrid $collapsed={isSubCollapsed}>
                          {subCategoryTopics.map((topic) => {
                            const isCompleted = topic._id ? completedTopics.has(topic._id) : false;
                            return (
                              <CompactTopicButton
                                key={topic.id}
                                onMouseEnter={(e) => handleTopicMouseEnter(topic, e)}
                                onMouseLeave={handleTopicMouseLeave}
                                onClick={() => handleTopicClick(topic)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                              >
                                <CompletionCheckbox
                                  $completed={isCompleted}
                                  style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                                  aria-label={isCompleted ? '已学习' : '未学习'}
                                >
                                  {isCompleted ? '✓' : ''}
                                </CompletionCheckbox>
                                <CompactTopicTitle>{topic.chineseTitle}</CompactTopicTitle>
                              </CompactTopicButton>
                            );
                          })}
                        </TopicGrid>
                      </div>
                    );
                  })}
                </TopicSection>
              );
            })}
          </TabContent>

          <TabContent id="learning-panel-practice" role="tabpanel" aria-labelledby="learning-tab-practice" active={activeTab === 'practice'} $isPractice={true}>
            <EditorContainer>
              <MonacoEditorComponent
                onRunCode={handleRunCode}
                onAskAI={handleAskAI}
              />
              <OutputPanel />
            </EditorContainer>
          </TabContent>

          <TabContent id="learning-panel-ai" role="tabpanel" aria-labelledby="learning-tab-ai" active={activeTab === 'ai-assistant'} $isPractice={false}>
            <ChatInterface />
          </TabContent>
        </GlassCard>
      </Container>

      {/* Topic Modal - Full-screen Interactive Card */}
      <ModalOverlay $visible={isModalVisible}>
        <ModalContainer $visible={isModalVisible}>
          {selectedTopic && (
            <>
              {/* Left Sidebar - Topic Navigation */}
              {!isContentFullscreen && (
                <TopicSidebar style={{
                  width: isSidebarCollapsed ? '60px' : '280px',
                  minWidth: isSidebarCollapsed ? '60px' : '280px',
                }}>
                {!isSidebarCollapsed && (
                  <SidebarHeader>
                    <SidebarTitle>📖 {learningCourse?.title || '学习主题'}</SidebarTitle>
                    <SidebarCloseButton onClick={handleCloseModal}>
                      ×
                    </SidebarCloseButton>
                  </SidebarHeader>
                )}

                <TopicList style={{
                  padding: isSidebarCollapsed ? '0.75rem 0' : '0.75rem 0',
                }}>
                  {learningCourse ? (
                    // Course-structured sidebar
                    <>
                      {learningCourse.chapters.map((chapter: any) => {
                        const chapterExpanded = expandedChapters.has(chapter._id);
                        const hasSections = Boolean(chapter.sections && chapter.sections.length > 0);
                        return (
                          <div key={chapter._id}>
                            {!isSidebarCollapsed && (
                              <TopicCategoryLabel
                                $color="#667eea"
                                onClick={() => toggleChapter(chapter._id)}
                              >
                                <CollapseIcon $collapsed={!chapterExpanded}>▼</CollapseIcon>
                                {chapter.title}
                              </TopicCategoryLabel>
                            )}
                            {chapterExpanded && !hasSections && chapter.topics?.map((topicItem: string | { topicId?: string; _id?: string }) => {
                              const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
                              if (!topicId) return null;
                              const topic = getTopicByMongoId(topicId);
                              if (!topic) return null;
                              const isActive = selectedTopic?.id === topic.id;
                              const isCompleted = completedTopics.has(topicId);
                              return (
                                <TopicListItem
                                  key={topicId}
                                  $active={isActive}
                                  onClick={() => handleTopicClick(topic, learningCourse)}
                                  title={isSidebarCollapsed ? topic.chineseTitle || topic.title : undefined}
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                >
                                  {!isSidebarCollapsed ? (
                                    <>
                                      <CompletionCheckbox
                                        $completed={isCompleted}
                                        style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                                        aria-label={isCompleted ? '已学习' : '未学习'}
                                      >
                                        {isCompleted ? '✓' : ''}
                                      </CompletionCheckbox>
                                      <TopicListItemText>{topic.chineseTitle || topic.title}</TopicListItemText>
                                    </>
                                  ) : (
                                    <TopicListItemText style={{ fontSize: '1.2rem', textAlign: 'center' }}>
                                      {isActive ? '●' : '○'}
                                    </TopicListItemText>
                                  )}
                                </TopicListItem>
                              );
                            })}
                            {chapterExpanded && hasSections && chapter.sections?.map((section: any) => {
                              const sectionExpanded = expandedSections.has(section._id);
                              return (
                                <div key={section._id}>
                                  {!isSidebarCollapsed && (
                                    <TopicCategoryLabel
                                      $color="#667eea"
                                      $isSubCategory={true}
                                      onClick={() => toggleSection(section._id)}
                                    >
                                      <CollapseIcon $collapsed={!sectionExpanded}>▼</CollapseIcon>
                                      {section.title}
                                    </TopicCategoryLabel>
                                  )}
                                  {sectionExpanded && section.topics?.map((topicItem: string | { topicId?: string; _id?: string }) => {
                                    const topicId = typeof topicItem === 'object' && topicItem !== null ? (topicItem.topicId || topicItem._id) : topicItem;
                                    if (!topicId) return null;
                                    const topic = getTopicByMongoId(topicId);
                                    if (!topic) return null;
                                    const isActive = selectedTopic?.id === topic.id;
                                    const isCompleted = completedTopics.has(topicId);
                                    return (
                                      <TopicListItem
                                        key={topicId}
                                        $active={isActive}
                                        onClick={() => handleTopicClick(topic, learningCourse)}
                                        title={isSidebarCollapsed ? topic.chineseTitle || topic.title : undefined}
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                      >
                                        {!isSidebarCollapsed ? (
                                          <>
                                            <CompletionCheckbox
                                              $completed={isCompleted}
                                              style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                                              aria-label={isCompleted ? '已学习' : '未学习'}
                                            >
                                              {isCompleted ? '✓' : ''}
                                            </CompletionCheckbox>
                                            <TopicListItemText>{topic.chineseTitle || topic.title}</TopicListItemText>
                                          </>
                                        ) : (
                                          <TopicListItemText style={{ fontSize: '1.2rem', textAlign: 'center' }}>
                                            {isActive ? '●' : '○'}
                                          </TopicListItemText>
                                        )}
                                      </TopicListItem>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    // Default full topic list sidebar
                    TOPICS_HIERARCHY.map((mainCategory) => {
                    const mainCategoryTopics = TOPICS_DATA.filter(t => t.mainCategoryCode === mainCategory.code);
                    if (mainCategoryTopics.length === 0) return null;

                    const isMainCollapsed = collapsedMainCategories.has(mainCategory.code);

                    return (
                      <div key={mainCategory.code}>
                        {!isSidebarCollapsed && (
                          <TopicCategoryLabel
                            $color={mainCategory.color}
                            onClick={() => toggleMainCategory(mainCategory.code)}
                          >
                            <CollapseIcon $collapsed={isMainCollapsed}>▼</CollapseIcon>
                            {mainCategory.chinese_name} ({mainCategory.topics_count}个主题)
                          </TopicCategoryLabel>
                        )}
                        {!isMainCollapsed && mainCategory.subcategories.map((subcategory) => {
                          const subCategoryTopics = TOPICS_DATA.filter(t => t.subCategoryCode === subcategory.code)
                            .map(topic => {
                              // Enrich with _id from mongoIdToTopicMap
                              const enriched = mongoIdToTopicMap.get(topic.id);
                              return enriched && enriched._id ? { ...topic, _id: enriched._id } : topic;
                            });
                          if (subCategoryTopics.length === 0) return null;

                          const isSubCollapsed = collapsedSubCategories.has(subcategory.code);

                          return (
                            <div key={subcategory.code}>
                              {!isSidebarCollapsed && (
                                <TopicCategoryLabel
                                  $color={mainCategory.color}
                                  $isSubCategory={true}
                                  onClick={() => toggleSubCategory(subcategory.code)}
                                >
                                  <CollapseIcon $collapsed={isSubCollapsed}>▼</CollapseIcon>
                                  {subcategory.chinese_name}
                                </TopicCategoryLabel>
                              )}
                              {!isSubCollapsed && subCategoryTopics.map((topic) => {
                                const isActive = selectedTopic?.id === topic.id;
                                const isCompleted = topic._id ? completedTopics.has(topic._id) : false;
                                return (
                                  <TopicListItem
                                    key={topic.id}
                                    $active={isActive}
                                    onClick={() => handleTopicSelect(topic.id)}
                                    title={isSidebarCollapsed ? topic.chineseTitle || topic.title : undefined}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                  >
                                    {!isSidebarCollapsed ? (
                                      <>
                                        <CompletionCheckbox
                                          $completed={isCompleted}
                                          style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                                          aria-label={isCompleted ? '已学习' : '未学习'}
                                        >
                                          {isCompleted ? '✓' : ''}
                                        </CompletionCheckbox>
                                        <TopicListItemText>{topic.chineseTitle || topic.title}</TopicListItemText>
                                      </>
                                    ) : (
                                      <TopicListItemText style={{ fontSize: '1.2rem', textAlign: 'center' }}>
                                        {isActive ? '●' : '○'}
                                      </TopicListItemText>
                                    )}
                                  </TopicListItem>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }))
                  }
                </TopicList>

                <SidebarToggleButton
                  $collapsed={isSidebarCollapsed}
                  onClick={handleToggleSidebar}
                >
                  {isSidebarCollapsed ? '▶' : '◀'} {isSidebarCollapsed ? '' : '收起'}
                </SidebarToggleButton>
              </TopicSidebar>
              )}

              {/* Main Content Area */}
              <ModalContentArea $fullscreen={isContentFullscreen}>
                <ModalHeader>
                  <HeaderInfo>
                    <ModalTitle>{selectedTopic.chineseTitle || selectedTopic.title}</ModalTitle>
                  </HeaderInfo>
                  <HeaderActions>
                    <FullscreenButton onClick={handleToggleFullscreen} title={isContentFullscreen ? '退出全屏' : '全屏显示'}>
                      {isContentFullscreen ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                      )}
                    </FullscreenButton>
                    {learningCourse && (
                      <>
                        <CourseNavButton
                          onClick={handlePreviousTopic}
                          disabled={!getPreviousTopicInCourse()}
                          title="上一个知识点"
                        >
                          <span>⬅️</span>
                          <span>上一个</span>
                        </CourseNavButton>
                        <CourseNavButton
                          onClick={handleNextTopic}
                          disabled={!getNextTopicInCourse()}
                          title="下一个知识点"
                        >
                          <span>下一个</span>
                          <span>➡️</span>
                        </CourseNavButton>
                      </>
                    )}
                    {canEditContent && !isEditMode && (
                      <EditButton onClick={handleEnterEditMode}>
                        <span>✏️</span>
                        <span>编辑内容</span>
                      </EditButton>
                    )}
                    {canEditContent && isEditMode && (
                      <>
                        <EditModeIndicator>编辑模式</EditModeIndicator>
                        <CancelButton onClick={handleCancelEdit}>
                          <span>✕</span>
                          <span>取消</span>
                        </CancelButton>
                        <EditButton
                          $isEditing={true}
                          onClick={handleSaveContent}
                          disabled={isSaving}
                        >
                          <span>{isSaving ? '⏳' : '💾'}</span>
                          <span>{isSaving ? '保存中...' : '保存'}</span>
                        </EditButton>
                      </>
                    )}
                  </HeaderActions>
                </ModalHeader>

                {/* Learning Start Overlay - covers content until student clicks start (only for students) */}
                {user?.primaryRole === 'student' && (
                  <LearningStartOverlay $visible={!learningStarted}>
                    <LearningStartContent>
                      <LearningStartTitle>{selectedTopic.chineseTitle || selectedTopic.title}</LearningStartTitle>
                      <LearningStartDescription>
                        准备好开始学习这个知识点了吗？点击下方按钮开始学习，我们将记录您的学习进度。
                      </LearningStartDescription>
                      <StartLearningButton onClick={handleStartLearning}>
                        <span>🚀</span>
                        <span>开始学习</span>
                      </StartLearningButton>
                    </LearningStartContent>
                  </LearningStartOverlay>
                )}

                <ModalContent data-content-area="true" onLoadCapture={e => {
                  if (e.target instanceof HTMLIFrameElement && selectedTopic?._id) setReadyTopicFrame(topicFrameKey);
                }}>
                  {renderModalContent(selectedTopic)}

                  {/* Learning Status Toggle Section - hidden in fullscreen */}
                  {!isContentFullscreen && (
                    <LearningStatusSection>
                      <LearningStatusInfo>
                        <LearningStatusIcon>📚</LearningStatusIcon>
                        <LearningStatusLabel>
                          <LearningStatusTitle>学习状态</LearningStatusTitle>
                          <LearningStatusDescription>
                            标记该知识点为已学习，方便跟踪您的学习进度
                          </LearningStatusDescription>
                        </LearningStatusLabel>
                      </LearningStatusInfo>
                      <LearningStatusSwitch
                        $learned={selectedTopic._id ? completedTopics.has(selectedTopic._id) : false}
                        onClick={() => selectedTopic._id && toggleTopicCompletion(selectedTopic._id)}
                      >
                        <LearningStatusIndicator $learned={selectedTopic._id ? completedTopics.has(selectedTopic._id) : false} />
                        <span>{selectedTopic._id && completedTopics.has(selectedTopic._id) ? '已学习' : '未学习'}</span>
                      </LearningStatusSwitch>
                    </LearningStatusSection>
                  )}

                  {/* Inline Self-Explanation Section - hidden in fullscreen */}
                  {!isContentFullscreen && (
                    <>
                      <SectionDivider />
                      <SelfExplanationSection onFocusCapture={() => setExplanationFocused(true)}
                        onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setExplanationFocused(false); }}>
                        <SelfExplanationHeader>
                          <SelfExplanationLabel>
                            <span>✍️</span>
                            <span>记录下您对该知识点的理解</span>
                          </SelfExplanationLabel>
                        </SelfExplanationHeader>
                        <InlineExplanationTextarea
                          value={selfExplanation}
                          onChange={(e) => setSelfExplanation(e.target.value)}
                          placeholder="用自己的话解释这个概念，帮助你更好地理解和记忆…

提示：
• 描述你对这个概念的理解
• 写出你认为最重要的知识点
• 举一个简单的例子来说明
• 记录任何疑问或需要进一步学习的地方"
                        />
                        <ExplanationActions>
                          {savedExplanation && !showSavedIndicator && (
                            <span style={{ fontSize: '0.85rem', color: glassTheme.colors.status.success }}>
                              ✓ 已于上次保存
                            </span>
                          )}
                          <SavedStatus $visible={showSavedIndicator}>
                            ✓ 保存成功
                          </SavedStatus>
                          <SelfExplanationSaveButton
                            $disabled={!selfExplanation.trim()}
                            onClick={handleSaveExplanation}
                          >
                            <span>💾</span>
                            <span>保存理解</span>
                          </SelfExplanationSaveButton>
                        </ExplanationActions>
                      </SelfExplanationSection>
                    </>
                  )}

                  {/* Quiz Section - hidden in fullscreen */}
                  {!isContentFullscreen && (
                    <QuizSection>
                      <QuizHeader>
                        <QuizLabel>
                          <span>📝</span>
                          <span>测验</span>
                        </QuizLabel>
                      </QuizHeader>
                      {selectedTopic.quiz && selectedTopic.quiz.length > 0 ? (
                        <>
                          <QuizLink $disabled={false} onClick={handleStartQuiz}>
                            <span>🔗</span>
                            <span>开始测验 →</span>
                          </QuizLink>
                          <QuizHint>
                            点击链接打开测验窗口，检验您的理解程度。共有 {selectedTopic.quiz.length} 道题目。
                          </QuizHint>
                        </>
                      ) : (
                        <QuizHint>该主题暂无测验题目。</QuizHint>
                      )}
                    </QuizSection>
                  )}
                </ModalContent>
              </ModalContentArea>
            </>
          )}
        </ModalContainer>
      </ModalOverlay>

      {/* Quiz Popup Modal */}
      {selectedTopic?.quiz && selectedTopic.quiz.length > 0 && (
        <QuizModalOverlay $visible={isQuizModalVisible}>
          <QuizModalContainer $visible={isQuizModalVisible}>
            <QuizModalHeader>
              <QuizModalTitle>
                <span>📝</span>
                <span>测验 - {selectedTopic.title}</span>
              </QuizModalTitle>
              {!showQuizResult && (
                <QuizProgress>
                  {currentQuestionIndex + 1} / {selectedTopic.quiz.length}
                </QuizProgress>
              )}
              <QuizModalCloseButton onClick={handleCloseQuizModal}>
                ×
              </QuizModalCloseButton>
            </QuizModalHeader>

            <QuizModalContent>
              {showQuizResult ? (
                <QuizResultSection $visible={showQuizResult}>
                  <QuizScore>{quizScore} / {selectedTopic.quiz.length}</QuizScore>
                  <QuizScoreLabel>
                    {quizPassed
                      ? '🎉 恭喜！您已连续答对3题，完成本主题学习！'
                      : quizScore === selectedTopic.quiz.length
                      ? '🎉 完美！你完全掌握了这个知识点！'
                      : quizScore >= selectedTopic.quiz.length * 0.6
                      ? '👍 做得不错！继续加油！'
                      : '💪 还需要再复习一下内容哦'}
                  </QuizScoreLabel>

                  {/* 连续正确次数显示 */}
                  <ConsecutiveCorrectDisplay $passed={quizPassed}>
                    <ConsecutiveCorrectTitle $passed={quizPassed}>
                      {quizPassed ? '🏆 已解锁！' : '🔥 连续正确'}
                    </ConsecutiveCorrectTitle>
                    <ConsecutiveCorrectCount $passed={quizPassed}>
                      {consecutiveCorrect}
                    </ConsecutiveCorrectCount>
                    <ConsecutiveCorrectDescription>
                      {quizPassed
                        ? '您已连续答对3题，成功掌握本主题！'
                        : `距离目标还需 ${Math.max(0, 3 - consecutiveCorrect)} 题`}
                    </ConsecutiveCorrectDescription>
                    <ProgressBar $progress={Math.min(100, (consecutiveCorrect / 3) * 100)}>
                      <ProgressBarFill
                        $progress={Math.min(100, (consecutiveCorrect / 3) * 100)}
                        $passed={quizPassed}
                      />
                    </ProgressBar>
                  </ConsecutiveCorrectDisplay>

                  <RetryButton onClick={handleRetryQuiz}>
                    <span>🔄</span>
                    <span>重新测验</span>
                  </RetryButton>
                </QuizResultSection>
              ) : (
                <>
                  <QuestionText>
                    {selectedTopic.quiz[currentQuestionIndex]?.question}
                  </QuestionText>
                  <QuizOptions>
                    {selectedTopic.quiz![currentQuestionIndex]?.options.map((option, idx) => {
                      const isSelected = quizAnswers[currentQuestionIndex] === idx;
                      const currentQ = selectedTopic.quiz![currentQuestionIndex];
                      const isCorrect = currentQ?.correctAnswer === idx;
                      const isWrong = isSelected && !isCorrect;

                      return (
                        <QuizOption
                          key={idx}
                          $selected={isSelected}
                          $correct={false}
                          $wrong={isWrong}
                          onClick={() => handleSelectAnswer(currentQuestionIndex, idx)}
                        >
                          <OptionLabel $selected={isSelected}>
                            {String.fromCharCode(65 + idx)}
                          </OptionLabel>
                          {option}
                        </QuizOption>
                      );
                    })}
                  </QuizOptions>
                </>
              )}
            </QuizModalContent>

            {!showQuizResult && (
              <QuizModalFooter>
                <QuizNavButtons>
                  <QuizNavButton
                    $variant="secondary"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                  >
                    ← 上一题
                  </QuizNavButton>
                  {currentQuestionIndex < selectedTopic.quiz.length - 1 ? (
                    <QuizNavButton
                      $variant="primary"
                      onClick={handleNextQuestion}
                      disabled={quizAnswers[currentQuestionIndex] === null}
                    >
                      下一题 →
                    </QuizNavButton>
                  ) : (
                    <QuizNavButton
                      $variant="primary"
                      onClick={handleSubmitQuiz}
                      disabled={quizAnswers.some(a => a === null)}
                    >
                      提交答案
                    </QuizNavButton>
                  )}
                </QuizNavButtons>
              </QuizModalFooter>
            )}
          </QuizModalContainer>
        </QuizModalOverlay>
      )}

      {/* Keyword Tooltip */}
      {keywordTooltip.visible && keywordTooltip.keyword && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2999,
              pointerEvents: 'auto'
            }}
            onClick={handleKeywordTooltipClose}
          />
          <KeywordTooltip
            $visible={keywordTooltip.visible}
            $x={keywordTooltip.x}
            $y={keywordTooltip.y}
          >
            <KeywordTooltipContent>
              <KeywordTerm $importance={keywordTooltip.keyword.importance}>
                {keywordTooltip.keyword.term}
                <KeywordImportanceBadge $level={keywordTooltip.keyword.importance}>
                  {keywordTooltip.keyword.importance === 'essential' ? '核心' :
                   keywordTooltip.keyword.importance === 'important' ? '重要' : '可选'}
                </KeywordImportanceBadge>
              </KeywordTerm>
              <KeywordDefinition>{keywordTooltip.keyword.definition}</KeywordDefinition>
              {keywordTooltip.keyword.aiExplanation ? (
                <KeywordAIExplanation>
                  💡 {keywordTooltip.keyword.aiExplanation}
                </KeywordAIExplanation>
              ) : (
                <GenerateAIButton onClick={() => keywordTooltip.keyword && handleGenerateAIExplanation(keywordTooltip.keyword)}>
                  <span>🤖</span>
                  <span>生成AI解释</span>
                </GenerateAIButton>
              )}
            </KeywordTooltipContent>
          </KeywordTooltip>
        </>
      )}

      {/* Course Description Tooltip */}
      {hoveredCourseTooltip && ReactDOM.createPortal(
        <span
          style={{
            position: 'fixed',
            top: `${hoveredCourseTooltip.rect.top - 8}px`,
            left: `${hoveredCourseTooltip.rect.left + hoveredCourseTooltip.rect.width / 2}px`,
            transform: 'translate(-50%, -100%)',
            padding: '0.4rem 0.6rem',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            zIndex: 9999999,
            fontWeight: 500,
            pointerEvents: 'none'
          }}
        >
          课程详情
        </span>,
        document.body
      )}

      {/* Course Tooltip */}
      {courseTooltip.visible && courseTooltip.course && (
        <CourseTooltip
          $visible={courseTooltip.visible}
          $x={courseTooltip.x}
          $y={courseTooltip.y}
        >
          <CourseTooltipTitle>{courseTooltip.course.title}</CourseTooltipTitle>
          <CourseTooltipMeta>
            <CourseTooltipMetaItem>
              <span>👨‍🏫 教师: {getTeacherName(courseTooltip.course)}</span>
            </CourseTooltipMetaItem>
            <CourseTooltipMetaItem>
              <span>📅 创建时间: {new Date(courseTooltip.course.createdAt).toLocaleDateString('zh-CN')}</span>
            </CourseTooltipMetaItem>
          </CourseTooltipMeta>
          {courseTooltip.course.description && (
            <CourseTooltipDescription>
              <strong>课程介绍:</strong><br />
              {courseTooltip.course.description}
            </CourseTooltipDescription>
          )}
        </CourseTooltip>
      )}

      {/* Course Enrollment Modal */}
      <EnrollModalOverlay $visible={showEnrollModal} onClick={closeEnrollModal}>
        <EnrollModalContainer
          $visible={showEnrollModal}
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="enroll-modal-title"
        >
          <EnrollModalTitle id="enroll-modal-title">
            📝 注册课程
          </EnrollModalTitle>
          <div style={{ marginBottom: '1rem', color: glassTheme.colors.text.secondary }}>
            请输入教师提供的课程代码以注册课程
          </div>
          <EnrollModalInput
            ref={enrollInputRef}
            type="text"
            aria-label="课程代码"
            aria-describedby={enrollError ? 'enroll-course-error' : undefined}
            placeholder="例如：STAT-2024-001"
            value={enrollCourseCode}
            onChange={e => setEnrollCourseCode(e.target.value.toUpperCase())}
            onKeyPress={e => {
              if (e.key === 'Enter' && enrollCourseCode.trim() && !enrolling) {
                handleEnrollCourse();
              }
            }}
            disabled={enrolling}
          />
          <EnrollModalError id="enroll-course-error" role="alert" $visible={!!enrollError}>
            {enrollError || 'Error'}
          </EnrollModalError>
          <EnrollModalActions>
            <EnrollButton
              onClick={() => {
                closeEnrollModal();
                setEnrollCourseCode('');
              }}
              disabled={enrolling}
            >
              取消
            </EnrollButton>
            <EnrollButton
              $primary
              onClick={handleEnrollCourse}
              disabled={!enrollCourseCode.trim() || enrolling}
            >
              {enrolling ? '注册中...' : '注册'}
            </EnrollButton>
          </EnrollModalActions>
        </EnrollModalContainer>
      </EnrollModalOverlay>

      {/* Course Description Modal */}
      <CourseDescModalOverlay $visible={showCourseDescModal} onClick={() => setShowCourseDescModal(false)}>
        <CourseDescModalContent onClick={e => e.stopPropagation()}>
          <CourseDescModalHeader>
            <CourseDescModalTitle>
              <span>📖</span>
              <span>课程介绍</span>
            </CourseDescModalTitle>
            <CourseDescModalClose aria-label="关闭课程介绍" onClick={() => setShowCourseDescModal(false)}>
              ×
            </CourseDescModalClose>
          </CourseDescModalHeader>
          <CourseDescModalBody>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', color: glassTheme.colors.text.primary }}>
              {selectedCourseDesc?.title}
            </h4>
            <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {selectedCourseDesc?.description}
            </p>
          </CourseDescModalBody>
        </CourseDescModalContent>
      </CourseDescModalOverlay>
    </DashboardLayout>
  );
};